// ════════════════════════════════════════════════════════════════════════════
// BLUE CARD EFFECTS
// See js/effects/registry.js for the registerCardEffect() API and the list
// of available triggers.
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// COMMAND & COMMAND PILOT EFFECTS
// ════════════════════════════════════════════════════════════════════════════

// ─── GD01-099 · Intercept Orders ─────────────────────────────────────────────
// [Burst] Choose 1 enemy Unit with 5 or less HP. Rest it.
// [Main]/[Action] Choose 1 to 2 enemy Units with 3 or less HP. Rest them.
registerCardEffect('GD01-099', {
  canActivate: () => state.oppField.some(c => c && !c.rested && effectiveHp(c) <= 3),
  burst: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Burst] no active enemy Units with 5 or less HP. No effect.`);
      ctx?.onDone?.(); return;
    }
    showCardPickerModal(
      card.name,
      '[Burst] Choose 1 active enemy Unit with 5 or less HP. Rest it.',
      candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${card.name} — [Burst] ${target.name} rested.`);
          render(); setupDropZones();
        }
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with 3 or less HP. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModalMulti(
      card.name,
      'Choose 1 to 2 active enemy Units with 3 or less HP. Rest them.',
      candidates,
      { min: 1, max: 2, mandatory: true },
      (targets) => {
        if (!targets || targets.length === 0) { ctx.onDone(); return; }
        targets.forEach(t => { setRested(t, true); addLog(`${card.name} — ${t.name} rested.`); });
        render(); setupDropZones();
        ctx.onDone();
      },
      card
    );
  },
  action: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with 3 or less HP. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModalMulti(
      card.name,
      'Choose 1 to 2 active enemy Units with 3 or less HP. Rest them.',
      candidates,
      { min: 1, max: 2, mandatory: true },
      (targets) => {
        if (!targets || targets.length === 0) { ctx.onDone(); return; }
        targets.forEach(t => { setRested(t, true); addLog(`${card.name} — ${t.name} rested.`); });
        render(); setupDropZones();
        ctx.onDone();
      },
      card
    );
  },
});

// ─── EB01-073 · Character Requests ───────────────────────────────────────────
// [Burst] Draw 1.
// [Main] If there are 6 or more rested Units in play, draw 2.
function eb01073RestedUnitCount() {
  return state.playerField.filter(c => c && c.rested).length + state.oppField.filter(c => c && c.rested).length;
}
registerCardEffect('EB01-073', {
  canActivate: () => eb01073RestedUnitCount() >= 6,
  burst: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
    ctx?.onDone?.();
  },
  main: (card, ctx) => {
    if (eb01073RestedUnitCount() < 6) {
      addLog(`${card.name} — fewer than 6 rested Units in play. No effect.`);
      ctx.onDone(); return;
    }
    const drawn = drawCardsFromTempDeck(2, true);
    drawn.forEach(c => state.hand.push(c));
    addLog(`${card.name} — 6+ rested Units in play: drew ${drawn.length} card${drawn.length !== 1 ? 's' : ''}.`);
    broadcastCounters();
    render();
    ctx.onDone();
  },
});

// ─── EB01-075 · Fierce Enemy Assault ─────────────────────────────────────────
// [Main]/[Action] Choose 1 to 2 enemy Units with 2 or less HP. Rest them.
function eb01075Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 2 or less HP. No effect.`);
    onDone(); return;
  }
  showCardPickerModalMulti(
    card.name,
    'Choose 1 to 2 active enemy Units with 2 or less HP. Rest them.',
    candidates,
    { min: 1, max: 2, mandatory: true },
    (targets) => {
      if (!targets || targets.length === 0) { onDone(); return; }
      targets.forEach(t => { setRested(t, true); addLog(`${card.name} — ${t.name} rested.`); });
      render(); setupDropZones();
      onDone();
    },
    card
  );
}
registerCardEffect('EB01-075', {
  canActivate: () => state.oppField.some(c => c && !c.rested && effectiveHp(c) <= 2),
  main:   (card, ctx) => eb01075Effect(card, ctx.onDone),
  action: (card, ctx) => eb01075Effect(card, ctx.onDone),
});

// ─── GD01-100 · A Show of Resolve ────────────────────────────────────────────
// [Main] Draw 2.
function gd01100Effect(card, onDone) {
  const drawn = drawCardsFromTempDeck(2, true);
  drawn.forEach(c => state.hand.push(c));
  addLog(`${card.name} — Drew ${drawn.length} card${drawn.length !== 1 ? 's' : ''}.`);
  broadcastCounters();
  render();
  onDone();
}
registerCardEffect('GD01-100', {
  main: (card, ctx) => gd01100Effect(card, ctx.onDone),
});
// ALT art — identical effect
registerCardEffect('GD01-100-ALT1', {
  main: (card, ctx) => gd01100Effect(card, ctx.onDone),
});

// ─── GD01-101 · Deep Devotion ─────────────────────────────────────────────────
// [Main]/[Action] Choose 1 friendly Link Unit. It recovers 3 HP.
// [Pilot] [Lucrezia Noin]
function gd01101Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && isLinkUnit(c));
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly Link Units in play. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(
    card.name,
    'Choose 1 friendly Link Unit. It recovers 3 HP.',
    candidates,
    (target) => {
      if (!target) { onDone(); return; }
      applyHpMod([target], 3);
      addLog(`${card.name} — ${target.name} recovers 3 HP (now: ${effectiveHp(target)}).`);
      render(); setupDropZones();
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD01-101', {
  canActivate: () => state.playerField.some(c => c && isLinkUnit(c)),
  main:   (card, ctx) => gd01101Effect(card, ctx.onDone),
  action: (card, ctx) => gd01101Effect(card, ctx.onDone),
});

// ─── GD01-102 · Securing the Supply Line ─────────────────────────────────────
// [Main] All friendly Units that are Lv.4 or lower recover 2 HP.
registerCardEffect('GD01-102', {
  canActivate: () => state.playerField.some(c => c && (parseInt(c.level) || 0) <= 4),
  main: (card, ctx) => {
    const targets = state.playerField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (targets.length === 0) {
      addLog(`${card.name} — no friendly Units Lv.4 or lower in play. No effect.`);
      ctx.onDone(); return;
    }
    applyHpMod(targets, 2);
    addLog(`${card.name} — ${targets.length} friendly Unit(s) Lv.4 or lower each recover 2 HP.`);
    render();
    ctx.onDone();
  },
});

// ─── GD01-103 · The Stubborn Cog ─────────────────────────────────────────────
// [Main] Choose 1 active friendly (Earth Federation) Unit and 1 active enemy
// Unit. Rest them both.
// [Pilot] [Daguza Mackle]
registerCardEffect('GD01-103', {
  canActivate: () =>
    state.playerField.some(u => u && !u.rested && (u.trait || '').includes('(Earth Federation)')) &&
    state.oppField.some(u => u && !u.rested),
  main: (card, ctx) => {
    const efCandidates = state.playerField.filter(u => u && !u.rested && (u.trait || '').includes('(Earth Federation)'));
    if (efCandidates.length === 0) {
      addLog(`${card.name} — no active friendly (Earth Federation) Units. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 active friendly (Earth Federation) Unit. It will be rested.',
      efCandidates,
      (friendlyUnit) => {
        if (!friendlyUnit) { ctx.onDone(); return; }
        const enemyCandidates = state.oppField.filter(u => u && !u.rested);
        if (enemyCandidates.length === 0) {
          addLog(`${card.name} — no active enemy Units to target. No effect.`);
          ctx.onDone(); return;
        }
        showCardPickerModal(
          card.name,
          'Choose 1 active enemy Unit. Both it and your (Earth Federation) Unit will be rested.',
          enemyCandidates,
          (enemyUnit) => {
            if (!enemyUnit) { ctx.onDone(); return; }
            setRested(friendlyUnit, true);
            setRested(enemyUnit, true);
            addLog(`${card.name} — ${friendlyUnit.name} and ${enemyUnit.name} are both rested.`);
            render(); setupDropZones();
            ctx.onDone();
          },
          card,
          { mandatory: true }
        );
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-104 · Signs of a Revolution ────────────────────────────────────────
// [Burst] Draw 1.
// [Main] Choose 1 rested enemy Unit. Deal 2 damage to it.
registerCardEffect('GD01-104', {
  canActivate: () => state.oppField.some(c => c && c.rested),
  burst: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
    ctx?.onDone?.();
  },
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 rested enemy Unit. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        dealDamageToUnit(target, 2, { fromEnemy: true });
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-100 · Dramatic Turnabout ───────────────────────────────────────────
// [Burst] Draw 1.
// [Main] Choose 1 friendly damaged Unit. It recovers 2 HP. Then, draw 1.
registerCardEffect('GD02-100', {
  canActivate: () => state.playerField.some(c => c && effectiveHp(c) < effectiveMaxHp(c)),
  burst: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
    ctx?.onDone?.();
  },
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && effectiveHp(c) < effectiveMaxHp(c));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly damaged Units in play. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly damaged Unit. It recovers 2 HP. Then draw 1.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        applyHpMod([target], 2);
        addLog(`${card.name} — ${target.name} recovers 2 HP (now: ${effectiveHp(target)}).`);
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length > 0) {
          state.hand.push(drawn[0]);
          addLog(`${card.name} — drew ${drawn[0].name}.`);
        }
        broadcastCounters(); render(); setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-101 · Beneath the Mask ─────────────────────────────────────────────
// [Main]/[Action] Choose 1 to 2 enemy Units that are Lv.2 or lower. Rest them.
function gd02101Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units Lv.2 or lower in play. No effect.`);
    onDone(); return;
  }
  showCardPickerModalMulti(
    card.name,
    'Choose 1 to 2 active enemy Units that are Lv.2 or lower. Rest them.',
    candidates,
    { min: 1, max: 2, mandatory: true },
    (targets) => {
      if (!targets || targets.length === 0) { onDone(); return; }
      targets.forEach(t => { setRested(t, true); addLog(`${card.name} — ${t.name} rested.`); });
      render(); setupDropZones();
      onDone();
    },
    card
  );
}
registerCardEffect('GD02-101', {
  canActivate: () => state.oppField.some(c => c && !c.rested && (parseInt(c.level) || 0) <= 2),
  main:   (card, ctx) => gd02101Effect(card, ctx.onDone),
  action: (card, ctx) => gd02101Effect(card, ctx.onDone),
});

// ─── GD02-102 · Mouar's Determination ────────────────────────────────────────
// [Main]/[Action] Choose 1 friendly (Titans) Unit. It gets AP+2 during this turn.
// [Pilot] [Mouar Pharaoh]
function gd02102Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Titans)'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly (Titans) Units in play. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(
    card.name,
    'Choose 1 friendly (Titans) Unit. It gets AP+2 during this turn.',
    candidates,
    (target) => {
      if (!target) { onDone(); return; }
      target.tempApMod = (target.tempApMod || 0) + 2;
      NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempApMod' });
      addLog(`${card.name} — ${target.name} gets AP+2 this turn.`);
      render(); setupDropZones();
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD02-102', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(Titans)')),
  main:   (card, ctx) => gd02102Effect(card, ctx.onDone),
  action: (card, ctx) => gd02102Effect(card, ctx.onDone),
});

// ─── GD03-101 · A Healthy Curiosity ──────────────────────────────────────────
// [Main] Draw 1. Then, if there are 2 or more cards with "A Healthy Curiosity"
// in their card name in your trash, choose 1 enemy Unit with 4 or less HP. Rest it.
// (The card is already in trash when main fires — ACTIVATE_COMMAND puts it there first.)
registerCardEffect('GD03-101', {
  main: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
    const trashCopies = state.playerTrash.filter(c => (c.name || '').includes('A Healthy Curiosity')).length;
    if (trashCopies >= 2) {
      const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 4);
      if (candidates.length === 0) {
        addLog(`${card.name} — 2+ copies in trash, but no active enemy Units with 4 or less HP.`);
        ctx.onDone(); return;
      }
      showCardPickerModal(
        card.name,
        `${trashCopies} copies in trash. Choose 1 active enemy Unit with 4 or less HP. Rest it.`,
        candidates,
        (target) => {
          if (!target) { ctx.onDone(); return; }
          setRested(target, true);
          addLog(`${card.name} — ${target.name} rested.`);
          render(); setupDropZones();
          ctx.onDone();
        },
        card,
        { mandatory: true }
      );
    } else {
      ctx.onDone();
    }
  },
});

// ─── GD03-102 · Privileged Position ──────────────────────────────────────────
// [Burst] Draw 1.
// [Action] Choose 1 of your (Titans) Link Units battling an enemy Unit. Set it
// as active.
registerCardEffect('GD03-102', {
  burst: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
    ctx?.onDone?.();
  },
  action: (card, ctx) => {
    const battleCtx = ctx.battle;
    if (!battleCtx) {
      addLog(`${card.name} — no active battle. No effect.`);
      ctx.onDone(); return;
    }
    // Identify the player's battling unit
    let battlingUnit = null;
    if (battleCtx.attackerSide === 'player') battlingUnit = battleCtx.attacker;
    else if (battleCtx.defenderSide === 'player') battlingUnit = battleCtx.defender;
    // Confirm: must be a (Titans) Link Unit
    if (!battlingUnit || !(battlingUnit.trait || '').includes('(Titans)') || !isLinkUnit(battlingUnit)) {
      addLog(`${card.name} — no friendly (Titans) Link Unit in the current battle. No effect.`);
      ctx.onDone(); return;
    }
    // Confirm: opponent must be battling a real unit (not shield/base/direct)
    const defender = battleCtx.defenderSide === 'player' ? battleCtx.attacker : battleCtx.defender;
    if (!defender || defender.isShield || defender.isDirect || !state.oppField.some(c => c && c.uid === defender.uid)) {
      addLog(`${card.name} — battle target is not an enemy field Unit. No effect.`);
      ctx.onDone(); return;
    }
    setRested(battlingUnit, false);
    addLog(`${card.name} — ${battlingUnit.name} is set as active.`);
    render(); setupDropZones();
    ctx.onDone();
  },
});

// ─── GD03-103 · Field Directive ───────────────────────────────────────────────
// [Burst] Choose 1 enemy Unit with 2 or less HP. Rest it.
// [Main] If 3 or more enemy Units are in play, choose 1 rested enemy Unit. Deal 2 damage.
registerCardEffect('GD03-103', {
  canActivate: () => {
    const enemyCount = state.oppField.filter(c => c != null).length;
    return enemyCount >= 3 && state.oppField.some(c => c && c.rested);
  },
  burst: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Burst] no active enemy Units with 2 or less HP. No effect.`);
      ctx?.onDone?.(); return;
    }
    showCardPickerModal(
      card.name,
      '[Burst] Choose 1 active enemy Unit with 2 or less HP. Rest it.',
      candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${card.name} — [Burst] ${target.name} rested.`);
          render(); setupDropZones();
        }
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
  main: (card, ctx) => {
    const enemyCount = state.oppField.filter(c => c != null).length;
    if (enemyCount < 3) {
      addLog(`${card.name} — fewer than 3 enemy Units in play (${enemyCount}). No effect.`);
      ctx.onDone(); return;
    }
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — 3+ enemies in play, but no rested enemy Units. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      `${enemyCount} enemy Units in play. Choose 1 rested enemy Unit. Deal 2 damage to it.`,
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        dealDamageToUnit(target, 2, { fromEnemy: true });
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-104 · Reccoa's Shadow ───────────────────────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit with 3 or less HP. Rest it.
// If a friendly (Jupitris) Link Unit is in play, choose 1 to 2 enemy Units with
// 3 or less HP instead.
// [Pilot] [Reccoa Londe]
function gd03104Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 3 or less HP. No effect.`);
    onDone(); return;
  }
  const hasJupitrisLink = state.playerField.some(u =>
    u && isLinkUnit(u) && (u.trait || '').includes('(Jupitris)')
  );
  if (hasJupitrisLink) {
    showCardPickerModalMulti(
      card.name,
      'Friendly (Jupitris) Link Unit in play. Choose 1 to 2 enemy Units with 3 or less HP. Rest them.',
      candidates,
      { min: 1, max: 2, mandatory: true },
      (targets) => {
        if (!targets || targets.length === 0) { onDone(); return; }
        targets.forEach(t => { setRested(t, true); addLog(`${card.name} — ${t.name} rested.`); });
        render(); setupDropZones();
        onDone();
      },
      card
    );
  } else {
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 3 or less HP. Rest it.',
      candidates,
      (target) => {
        if (!target) { onDone(); return; }
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render(); setupDropZones();
        onDone();
      },
      card,
      { mandatory: true }
    );
  }
}
registerCardEffect('GD03-104', {
  canActivate: () => state.oppField.some(c => c && !c.rested && effectiveHp(c) <= 3),
  main:   (card, ctx) => gd03104Effect(card, ctx.onDone),
  action: (card, ctx) => gd03104Effect(card, ctx.onDone),
});

// ─── GD04-101 · Kindhearted ────────────────────────────────────────────────────
// [Burst] Activate this card's [Main].
// [Main]/[Action] During this turn, friendly Units can't be destroyed by direct
// enemy effects (like "Fatal Strike" — bypasses HP). Then, draw 1.
// Sets tempImmuneToEffectDestruction on all current playerField units; synced to
// the opponent's oppField mirror via FIELD_AP_MOD. Checked in effectDestroyEnemyUnit
// (purple.js). Cleared by CLEAR_TURN_EFFECTS at end of this turn.
function gd04101Effect(card, onDone) {
  const units = state.playerField.filter(u => u);
  units.forEach(u => { u.tempImmuneToEffectDestruction = true; });
  if (units.length > 0) {
    NET.send({ type: 'FIELD_AP_MOD', uids: units.map(u => u.uid), delta: 1, field: 'tempImmuneToEffectDestruction' });
  }
  addLog(`${card.name} — friendly Units can't be directly destroyed by enemy effects this turn.`);
  const drawn = drawCardsFromTempDeck(1, true);
  if (drawn.length > 0) {
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name}.`);
  }
  broadcastCounters(); render(); setupDropZones();
  onDone();
}
registerCardEffect('GD04-101', {
  burst: (card, ctx) => gd04101Effect(card, ctx?.onDone ?? (() => {})),
  main:   (card, ctx) => gd04101Effect(card, ctx.onDone),
  action: (card, ctx) => gd04101Effect(card, ctx.onDone),
});

// ─── GD04-102 · Moment of Rest ────────────────────────────────────────────────
// [Burst] Draw 1.
// [Main] Choose 1 rested enemy Unit that is Lv.5 or lower. It won't be set as
// active during the start phase of your opponent's next turn.
// Sets keepRestedNextTurn on the oppField target; synced via FIELD_AP_MOD.
// UNREST_ALL (game.js + network.js) skips the unit's unrest if flag is set.
registerCardEffect('GD04-102', {
  canActivate: () => state.oppField.some(c => c && c.rested && (parseInt(c.level) || 0) <= 5),
  burst: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
    ctx?.onDone?.();
  },
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && c.rested && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units Lv.5 or lower. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      "Choose 1 rested enemy Unit (Lv.5 or lower). It won't unrest at start of opponent's next turn.",
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        target.keepRestedNextTurn = true;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'keepRestedNextTurn' });
        addLog(`${card.name} — ${target.name} will stay rested at start of opponent's next turn.`);
        render(); setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── EB01-001 · Gundam Astray Red Frame Custom (EX) ──────────────────────────
// [Activate･Main][Once per Turn] Exile 2 Command cards from your trash from
// the game: Choose 1 damaged enemy Unit that is Lv.7 or lower. Rest it. It
// won't be set as active during the start phase of your opponent's next
// turn (still can be set active by card effects).
// No RES cost (getUnitActivationCost parses 0 — cost.res gate is trivially
// satisfied); the Command-card exile cost is checked/paid by hand here,
// gated additionally via canActivate. Once-per-turn + Main-Phase gating are
// handled generically by canActivateUnitEffect/showUnitActivateModal.
// keepRestedNextTurn mechanic reused from GD04-102 Moment of Rest.
registerCardEffect('EB01-001', {
  canActivate: (card) => {
    const cmdCount = state.playerTrash.filter(c => c.type === 'command' || c.type === 'command pilot').length;
    if (cmdCount < 2) return false;
    return state.oppField.some(c => c && c.damaged && (parseInt(c.level) || 0) <= 7);
  },
  activate: (card, ctx) => {
    const cmdCandidates = state.playerTrash.filter(c => c.type === 'command' || c.type === 'command pilot');
    if (cmdCandidates.length < 2) {
      addLog(`${card.name} — need 2 Command cards in trash to activate. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModalMulti(
      card.name,
      'Exile 2 Command cards from your trash.',
      cmdCandidates,
      { min: 2, max: 2, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 2) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        const targets = state.oppField.filter(c => c && c.damaged && (parseInt(c.level) || 0) <= 7);
        if (targets.length === 0) {
          addLog(`${card.name} — no damaged enemy Unit Lv.7 or lower. No further effect.`);
          broadcastCounters(); render(); setupDropZones();
          ctx.onDone(); return;
        }
        showCardPickerModal(
          card.name,
          'Choose 1 damaged enemy Unit that is Lv.7 or lower. Rest it.',
          targets,
          (target) => {
            if (target) {
              setRested(target, true);
              target.keepRestedNextTurn = true;
              NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'keepRestedNextTurn' });
              addLog(`${card.name} — rested ${target.name}. It won't be set active during the opponent's next Start Phase.`);
              render(); setupDropZones();
            }
            broadcastCounters();
            ctx.onDone();
          },
          card,
          { mandatory: true }
        );
      },
      card
    );
  },
});

// ─── GD04-103 · Spiritual Support ─────────────────────────────────────────────
// [Main] Choose 1 of your Units. It gains <Repair 2> during this turn.
// (getRepairValue checks tempKeywords first, so this grants end-phase healing.)
registerCardEffect('GD04-103', {
  canActivate: () => state.playerField.some(c => c),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your Units. It gains <Repair 2> during this turn.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('Repair 2');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Repair 2' });
        addLog(`${card.name} — ${target.name} gains <Repair 2> this turn.`);
        render(); setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-104 · Shrike Team's Bulwark ────────────────────────────────────────
// [Main]/[Action] Choose 1 to 2 enemy Units that are Lv.2 or lower. Rest them.
// [Pilot] [Junko Jenko]
// (Same effect as GD02-101 Beneath the Mask)
registerCardEffect('GD04-104', {
  canActivate: () => state.oppField.some(c => c && !c.rested && (parseInt(c.level) || 0) <= 2),
  main:   (card, ctx) => gd02101Effect(card, ctx.onDone),
  action: (card, ctx) => gd02101Effect(card, ctx.onDone),
});

// ─── ST01-012 · Thoroughly Damaged ───────────────────────────────────────────
// [Main] Choose 1 rested enemy Unit. Deal 1 damage to it.
// [Pilot] [Hayato Kobayashi]
registerCardEffect('ST01-012', {
  canActivate: () => state.oppField.some(c => c && c.rested),
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 rested enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        dealDamageToUnit(target, 1, { fromEnemy: true });
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST01-013 · Kai's Resolve ─────────────────────────────────────────────────
// [Main] Choose 1 friendly Unit. It recovers 3 HP.
// [Pilot] [Kai Shiden]
registerCardEffect('ST01-013', {
  canActivate: () => state.playerField.some(c => c),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly Unit. It recovers 3 HP.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        applyHpMod([target], 3);
        addLog(`${card.name} — ${target.name} recovers 3 HP (now: ${effectiveHp(target)}).`);
        render(); setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST02-014 · Siege Ploy ────────────────────────────────────────────────────
// [Burst] Activate this card's [Main].
// [Main]/[Action] Choose 1 enemy Unit with 5 or less HP. Rest it.
function st02014Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 5);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 5 or less HP. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(
    card.name,
    'Choose 1 active enemy Unit with 5 or less HP. Rest it.',
    candidates,
    (target) => {
      if (!target) { onDone(); return; }
      setRested(target, true);
      addLog(`${card.name} — ${target.name} rested.`);
      render(); setupDropZones();
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('ST02-014', {
  canActivate: () => state.oppField.some(c => c && !c.rested && effectiveHp(c) <= 5),
  burst: (card, ctx) => st02014Effect(card, ctx?.onDone ?? (() => {})),
  main:   (card, ctx) => st02014Effect(card, ctx.onDone),
  action: (card, ctx) => st02014Effect(card, ctx.onDone),
});

// ─── GD01-123 · Nahel Argama ────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand (generic). Then, choose 1 enemy Unit with
// 3 or less HP. Rest it.
registerCardEffect('GD01-123', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 active enemy Unit with 3 or less HP. Rest it.',
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-124 · Side 7 ──────────────────────────────────────────────────────
// [Activate･Main] Rest this Base: Choose 1 friendly Unit. It recovers 1 HP.
registerCardEffect('GD01-124', {
  canActivate: () => state.playerField.some(c => c != null),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c != null);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly Unit. It recovers 1 HP.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        applyHpMod([target], 1);
        addLog(`${card.name} — ${target.name} recovers 1 HP (now: ${effectiveHp(target)}).`);
        render();
        setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST01-015 · White Base ───────────────────────────────────────────────────
// [Activate･Main][Once per Turn]②: Deploy a WBT token based on how many
// friendly Units are in play — 0 Units: Gundam (T-001), 1 Unit: Guncannon
// (T-002), 2+ Units: Guntank (T-003).
registerCardEffect('ST01-015', {
  canActivate: () => findNextFieldSlot() !== -1,
  activate: (card, ctx) => {
    const unitCount = state.playerField.filter(c => c != null).length;
    let tokenId, tokenDesc;
    if (unitCount === 0) {
      tokenId = 'T-001'; tokenDesc = 'Gundam (AP3/HP3)';
    } else if (unitCount === 1) {
      tokenId = 'T-002'; tokenDesc = 'Guncannon (AP2/HP2)';
    } else {
      tokenId = 'T-003'; tokenDesc = 'Guntank (AP1/HP1)';
    }
    const token = deployTokenToField(tokenId);
    if (!token) {
      addLog(`${card.name} — field is full, no token deployed.`);
      ctx.onDone(); return;
    }
    addLog(`${card.name} — deployed ${tokenDesc} token (${unitCount} other Units in play).`);
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// ─── ST02-016 · Corsica Base ─────────────────────────────────────────────────
// [Deploy] During your turn: if "Corsica Base" is in your trash, deploy
// 2 Leo (T-004) tokens; otherwise deploy 1 Tallgeese (T-005) token.
registerCardEffect('ST02-016', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const hasCorsicaInTrash = state.playerTrash.some(c => (c.name || '').includes('Corsica Base'));
    if (hasCorsicaInTrash) {
      const t1 = deployTokenToField('T-004');
      if (t1) addLog(`${card.name} — deployed Leo token (1 of 2).`);
      const t2 = deployTokenToField('T-004');
      if (t2) addLog(`${card.name} — deployed Leo token (2 of 2).`);
      else addLog(`${card.name} — no empty slot for second Leo token.`);
    } else {
      const token = deployTokenToField('T-005');
      if (token) addLog(`${card.name} — deployed Tallgeese token.`);
      else addLog(`${card.name} — no empty field slot for Tallgeese token.`);
    }
    render();
    setupDropZones();
  },
});

// ─── GD02-121 · Dominion ─────────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand (generic). Then, choose 1 friendly blue Unit.
// It recovers 2 HP.
registerCardEffect('GD02-121', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.color || '').toLowerCase() === 'blue');
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 friendly blue Unit. It recovers 2 HP.',
      candidates,
      (target) => {
        if (!target) return;
        applyHpMod([target], 2);
        addLog(`${card.name} — ${target.name} recovers 2 HP (now: ${effectiveHp(target)}).`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST10-013 · Tactical Training ────────────────────────────────────────────
// [Main]/[Action] Choose 1 (G Generation) Unit that is Lv.5 or higher. It
// recovers 2 HP and gets AP+2 during this turn.
function st10013Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)') && (parseInt(c.level) || 0) >= 5);
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly (G Generation) Unit Lv.5 or higher. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 (G Generation) Unit that is Lv.5 or higher. It recovers 2 HP and gets AP+2 during this turn.', candidates, (target) => {
    if (target) {
      applyHpMod([target], 2);
      applyApMod([target], 2, 'tempApMod');
      addLog(`${card.name} — ${target.name} recovers 2 HP and gets AP+2 during this turn (now: ${effectiveHp(target)} HP).`);
      render();
      setupDropZones();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('ST10-013', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(G Generation)') && (parseInt(c.level) || 0) >= 5),
  main:   (card, ctx) => st10013Effect(card, ctx.onDone),
  action: (card, ctx) => st10013Effect(card, ctx.onDone),
});

// ─── ST10-014 · Unlocking the Development Diagram ────────────────────────────
// While playing this card from your hand, you may discard 1 (G Generation)
// Unit card. If you do, play this card as if it has 2 Lv. and cost.
// [Main] Draw 2.
//
// prePlayFromHand runs in handleDrop (game.js) BEFORE the level/cost gate,
// for any card type — not just Units auto-placed at an empty slot — so the
// discard choice here can actually make the gate pass. Setting
// card._altCostDiscarded makes handLevelReduction/handCostReduction return
// the full 2-point reduction, same convention as GD01-002 Unicorn Gundam's
// destroy-mode discount. altCostPreview lets canPlayCard's hand-dimming
// check see the discounted level/cost too, so the card isn't shown dimmed
// when the discount would make it playable.
registerCardEffect('ST10-014', {
  altCostPreview: (card) => {
    const hasCandidate = state.hand.some(c =>
      c && c.uid !== card.uid && c.type === 'unit' && (c.trait || '').includes('(G Generation)')
    );
    return hasCandidate ? { level: 2, cost: 2 } : null;
  },
  prePlayFromHand: (card, _slot, onProceed) => {
    const candidates = state.hand.filter(c =>
      c && c.uid !== card.uid && c.type === 'unit' && (c.trait || '').includes('(G Generation)')
    );
    if (candidates.length === 0) { onProceed(); return; }
    showCardPickerModal(
      card.name,
      'You may discard 1 (G Generation) Unit card. If you do, play this card as if it has 2 Lv. and cost.',
      candidates,
      (chosen) => {
        if (chosen) {
          card._altCostDiscarded = true;
          dispatch('DISCARD_CARD', { uid: chosen.uid, source: 'hand' });
          broadcastLog(`${card.name} — discarded ${chosen.name} as alternate cost.`);
          broadcastCounters(); render(); setupDropZones();
        }
        onProceed();
      },
      card
    );
  },
  handLevelReduction: (card) =>
    card._altCostDiscarded ? Math.max(0, (parseInt(card.level) || 0) - 2) : 0,
  handCostReduction: (card) =>
    card._altCostDiscarded ? Math.max(0, (parseInt(card.cost) || 0) - 2) : 0,
  main: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(2, true);
    drawn.forEach(c => state.hand.push(c));
    addLog(`${card.name} — drew ${drawn.length} card${drawn.length !== 1 ? 's' : ''}.`);
    broadcastCounters();
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// ─── ST10-016 · Luna Mana & Carry Base ───────────────────────────────────────
// [Deploy] Add 1 Shield to hand (generic). Then, all friendly (G Generation)
// Units recover 1 HP.
registerCardEffect('ST10-016', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)'));
    if (candidates.length === 0) return;
    applyHpMod(candidates, 1);
    addLog(`${card.name} — all friendly (G Generation) Units recover 1 HP.`);
    render();
    setupDropZones();
  },
});

// ─── EB01-074 · Eternal Road ─────────────────────────────────────────────────
// [Burst] Choose 1 active enemy Unit with 3 or less HP. Rest it.
// [Main]/[Action] Choose 1 active friendly (G Generation) Unit. Rest it. If
// you do, all enemy players each choose 1 of their active Units. Rest them.
// (This engine has no opponent-choice networking, so the activating player
// picks the enemy target — same convention as every other "choose 1 enemy
// Unit" effect in this codebase.)
function eb01074BurstEffect(card, onDone) {
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
  if (candidates.length === 0) {
    addLog(`${card.name} — [Burst] no active enemy Unit with 3 or less HP. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(card.name, '[Burst] Choose 1 active enemy Unit with 3 or less HP. Rest it.', candidates, (target) => {
    if (target) {
      setRested(target, true);
      addLog(`${card.name} — [Burst] rested ${target.name}.`);
      render();
      setupDropZones();
    }
    onDone();
  }, card, { mandatory: true });
}
function eb01074Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && !c.rested && (c.trait || '').includes('(G Generation)'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no active friendly (G Generation) Units. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 active friendly (G Generation) Unit. Rest it.', candidates, (friendly) => {
    if (!friendly) { onDone(); return; }
    setRested(friendly, true);
    addLog(`${card.name} — rested ${friendly.name}.`);
    const enemyCandidates = state.oppField.filter(c => c && !c.rested);
    if (enemyCandidates.length === 0) {
      addLog(`${card.name} — no active enemy Units to rest.`);
      render();
      setupDropZones();
      onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 active enemy Unit. Rest it.', enemyCandidates, (enemy) => {
      if (enemy) {
        setRested(enemy, true);
        addLog(`${card.name} — rested ${enemy.name} (enemy).`);
      }
      render();
      setupDropZones();
      onDone();
    }, card, { mandatory: true });
  }, card, { mandatory: true });
}
registerCardEffect('EB01-074', {
  canActivate: () => state.playerField.some(c => c && !c.rested && (c.trait || '').includes('(G Generation)')),
  burst:  (card, ctx) => eb01074BurstEffect(card, ctx?.onDone ?? (() => {})),
  main:   (card, ctx) => eb01074Effect(card, ctx.onDone),
  action: (card, ctx) => eb01074Effect(card, ctx.onDone),
});

// ─── EB01-076 · Gerbera Straight ─────────────────────────────────────────────
// [Main]/[Action] Choose 1 friendly (G Generation) Unit. It recovers 3 HP.
// [Pilot] Lowe Guele
function eb01076Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly (G Generation) Units in play. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 friendly (G Generation) Unit. It recovers 3 HP.', candidates, (target) => {
    if (target) {
      applyHpMod([target], 3);
      addLog(`${card.name} — ${target.name} recovers 3 HP (now: ${effectiveHp(target)}).`);
      render();
      setupDropZones();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('EB01-076', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(G Generation)')),
  main:   (card, ctx) => eb01076Effect(card, ctx.onDone),
  action: (card, ctx) => eb01076Effect(card, ctx.onDone),
});

// ─── EB01-085 · Kudelia Aina Bernstein & Isaribi ─────────────────────────────
// [Deploy] Add 1 Shield to hand (generic). Then, you may choose 1 active
// friendly blue (G Generation) Unit and 1 active enemy Unit. Rest them.
registerCardEffect('EB01-085', {
  deploy: (card) => {
    const friendlies = state.playerField.filter(c => c && !c.rested && (c.color || '').toLowerCase() === 'blue' && (c.trait || '').includes('(G Generation)'));
    const enemies = state.oppField.filter(c => c && !c.rested);
    if (friendlies.length === 0 || enemies.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 active friendly blue (G Generation) Unit to rest (or skip).',
      friendlies,
      (friendly) => {
        if (!friendly) return;
        showCardPickerModal(
          card.name,
          'Choose 1 active enemy Unit. Rest it along with the chosen friendly Unit.',
          enemies,
          (enemy) => {
            if (!enemy) return;
            setRested(friendly, true);
            setRested(enemy, true);
            addLog(`${card.name} — rested ${friendly.name} and ${enemy.name}.`);
            render();
            setupDropZones();
          },
          card,
          { mandatory: true }
        );
      },
      card,
      { mandatory: false }
    );
  },
});

// ─── EB01-086 · Kycilia Zabi & Gwazine ───────────────────────────────────────
// [Deploy] Add 1 Shield to hand (generic).
// [Once per Turn] During your turn, when a friendly (G Generation) Unit
// links, it gains <Repair 2> during this turn.
// Fires via the onPlayerUnitLinked hook in game.js PAIR_PILOT.
registerCardEffect('EB01-086', {
  onPlayerUnitLinked: (base, unit, slot) => {
    if (!state.myTurn) return;
    if (base.passiveUsedThisTurn) return;
    if (!(unit.trait || '').includes('(G Generation)')) return;
    base.passiveUsedThisTurn = true;
    unit.tempKeywords = unit.tempKeywords || [];
    unit.tempKeywords.push('Repair 2');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'Repair 2' });
    addLog(`${base.name} — ${unit.name} linked; gains <Repair 2> this turn.`);
    render();
  },
});

// ─── GD02-122 · Alexandria ───────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand (generic). Then, choose 1 rested enemy Unit
// that is Lv.4 or lower. Deal 1 damage to it.
registerCardEffect('GD02-122', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && c.rested && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 rested enemy Unit that is Lv.4 or lower. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST08-015 · Davao ────────────────────────────────────────────────────────
// [Activate･Main][Once per Turn]②: Choose 1 of your Units. It recovers 2 HP.
registerCardEffect('ST08-015', {
  canActivate: () => state.playerField.some(c => c != null),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c != null);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your Units. It recovers 2 HP.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        applyHpMod([target], 2);
        addLog(`${card.name} — ${target.name} recovers 2 HP (now: ${effectiveHp(target)}).`);
        render();
        setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-123 · Jupitris ─────────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand (generic). Then, if a friendly (Jupitris) Unit
// is in play, choose 1 enemy Unit that is Lv.3 or lower. Rest it.
registerCardEffect('GD03-123', {
  deploy: (card) => {
    const hasJupitris = state.playerField.some(c => c && (c.trait || '').includes('(Jupitris)'));
    if (!hasJupitris) return;
    const candidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 active enemy Unit that is Lv.3 or lower. Rest it.',
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-124 · Ribo Colony ──────────────────────────────────────────────────
// [Once per Turn] When you pair a Pilot that is Lv.3 or lower with one of your
// Units, choose 1 enemy Unit with 3 or less HP. Rest it.
// Uses the onPilotPaired hook added to game.js PAIR_PILOT action.
registerCardEffect('GD03-124', {
  onPilotPaired: (base, pilot, pairedUnit, slot) => {
    if (!state.myTurn) return;
    if (base.passiveUsedThisTurn) return;
    const pilotLevel = parseInt(pilot.level) || 0;
    if (pilotLevel > 3) return;
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${base.name} — ${pilot.name} paired (Lv.${pilotLevel}), but no active enemy Units with 3 or less HP.`);
      return;
    }
    base.passiveUsedThisTurn = true;
    showCardPickerModal(
      base.name,
      `${pilot.name} (Lv.${pilotLevel}) was paired. Choose 1 active enemy Unit with 3 or less HP. Rest it.`,
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${base.name} — ${target.name} rested (${pilot.name} Lv.${pilotLevel} paired).`);
        render();
        setupDropZones();
      },
      base,
      { mandatory: true }
    );
  },
});

// ─── T-021 · Parts token ─────────────────────────────────────────────────────
// This Unit can't choose non-Unit targets (base, shields, direct).
registerCardEffect('T-021', {
  cantAttackNonUnit: () => true,
});

// ════════════════════════════════════════════════════════════════════════════
// PILOT EFFECTS
// ════════════════════════════════════════════════════════════════════════════

// ─── GD01-087 · Sayla Mass ────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit is blue, it gains <Repair 1>.
// pilotConditionalRepairValue drives end-of-turn Repair; pilotPassiveKeywords
// drives the Repair icon display via hasKwIcon().
registerCardEffect('GD01-087', {
  pilotConditionalRepairValue: (pilot, unit) =>
    (unit.color || '').toLowerCase() === 'blue' ? 1 : 0,
  pilotPassiveKeywords: (pilot, unit) =>
    (unit.color || '').toLowerCase() === 'blue' ? ['Repair 1'] : [],
});

// ─── GD01-088 · Banagher Links (+ ALT1) ──────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] If this is a Link Unit, draw 1.
function gd01088Effect(card, ctx) {
  const unit = ctx?.unit;
  if (!unit || !isLinkUnit(unit)) return;
  const drawn = drawCardsFromTempDeck(1, true);
  if (drawn.length > 0) {
    state.hand.push(drawn[0]);
    addLog(`${card.name} — [When Paired] Link Unit: drew ${drawn[0].name}.`);
  } else {
    addLog(`${card.name} — deck is empty, no draw.`);
  }
  broadcastCounters();
  render();
}
registerCardEffect('GD01-088', { pair: gd01088Effect });
registerCardEffect('GD01-088-ALT1', { pair: gd01088Effect });

// ─── GD01-089 · Riddhe Marcenas ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// If this Unit has <Repair>, it gets AP+1.
registerCardEffect('GD01-089', {
  passiveApBonus: (pilot, unit) => hasKwIcon(unit, 'Repair') ? 1 : 0,
});

// ─── GD02-085 · Four Murasame ─────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Once per Turn] During your turn, when this Unit recovers HP,
// if you have 4 or less cards in hand, draw 1.
registerCardEffect('GD02-085', {
  onUnitHpRecovered: (pilot, unit) => {
    if (!isLinkUnit(unit)) return;
    if (!state.myTurn) return;
    if (pilot.pilotEffectUsedThisTurn) return;
    if (state.hand.length > 4) return;
    pilot.pilotEffectUsedThisTurn = true;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${pilot.name} — [During Link][Once per Turn] recovered HP with ≤4 cards in hand: drew ${drawn[0].name}.`);
    } else {
      addLog(`${pilot.name} — deck is empty, no draw.`);
    }
    broadcastCounters();
    render();
  },
});

// ─── EB01-004 · Gundam Barbatos Lupus Rex (EX) ────────────────────────────
// <Repair 2> (native, handled generically by getRepairValue/REPAIR_FIELD).
// [Once per Turn] During your turn, when this Unit recovers HP, choose 1
// rested enemy Unit. Deal 1 damage to it.
// Uses the new unit-level onSelfHpRecovered hook (added to applyHpMod in
// white.js and REPAIR_FIELD in game.js) — fires for HP recovery from either
// source, independent of any paired pilot (unlike onUnitHpRecovered).
registerCardEffect('EB01-004', {
  onSelfHpRecovered: (card) => {
    if (!state.myTurn) return;
    if (card.passiveUsedThisTurn) return;
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      return;
    }
    card.passiveUsedThisTurn = true;
    showCardPickerModal(card.name, 'Choose 1 rested enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — recovered HP: dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD02-086 · Jercardo Messa ────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While you have another (Titans) Unit in play, this gets AP+1.
registerCardEffect('GD02-086', {
  passiveApBonus: (pilot, unit) => {
    const ownerSide = state.playerField.some(c => c && c.uid === unit.uid) ? 'player' : 'opp';
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    return field.some(c => c && c.uid !== unit.uid && (c.trait || '').includes('(Titans)')) ? 1 : 0;
  },
});

// ─── GD02-087 · Orga, Crot, and Shani ────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] If this is a blue Unit, choose 1 enemy Unit with <Blocker>. Rest it.
registerCardEffect('GD02-087', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    if ((unit.color || '').toLowerCase() !== 'blue') {
      addLog(`${card.name} — Unit is not blue; no effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c && !c.rested && hasNativeKeyword(c, 'Blocker'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with <Blocker> in play.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Linked] Choose 1 active enemy Unit with <Blocker>. Rest it.',
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-084 · Paptimus Scirocco ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] Choose 1 of your other Units. It gains <Repair 2> during this
// turn. Then, if it is a (Jupitris) Unit, draw 1.
registerCardEffect('GD03-084', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    const candidates = state.playerField.filter(c => c && c.uid !== unit.uid);
    if (candidates.length === 0) {
      addLog(`${card.name} — no other friendly Units in play.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Linked] Choose 1 of your other Units. It gains <Repair 2> this turn. If (Jupitris), draw 1.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Repair 2')) target.tempKeywords.push('Repair 2');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Repair 2' });
        addLog(`${card.name} — ${target.name} gains <Repair 2> this turn.`);
        if ((target.trait || '').includes('(Jupitris)')) {
          const drawn = drawCardsFromTempDeck(1, true);
          if (drawn.length > 0) {
            state.hand.push(drawn[0]);
            addLog(`${card.name} — (Jupitris) Unit: drew ${drawn[0].name}.`);
          } else {
            addLog(`${card.name} — deck is empty, no draw.`);
          }
          broadcastCounters();
        }
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-085 · Christina Mackenzie ──────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// When paired with a Unit with "Gundam NT-1" in its name, this card costs 0.
// pilotCostOverride is checked in PAIR_PILOT before spendResources is called.
registerCardEffect('GD03-085', {
  pilotCostOverride: (pilot, unitCard) =>
    (unitCard.name || '').includes('Gundam NT-1') ? 0 : (parseInt(pilot.cost) || 0),
});

// Shared helper: determines which side owns a card at 'destroyed' trigger
// time, across both possible ctx shapes — the battle-destroy path (ctx =
// {attacker, attackerSide, defenderSide, defender, ...}, no ownerSide field)
// and the effect-damage-destroy path (ctx = { ownerSide, unit }, no
// attacker/defender fields).
function destroyedOwnerSide(card, ctx) {
  if (!ctx) return null;
  if (ctx.ownerSide) return ctx.ownerSide;
  if (ctx.defender || ctx.attacker) {
    return card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
  }
  return null;
}

// ─── EB01-015 · Prototype Asshimar TR-3 "Kehaar" ─────────────────────────
// [Destroyed] If there are 2 or more other rested Units in play, choose 1
// rested enemy Unit. Deal 1 damage to it.
// Fires from both destroy paths (battle damage and effect damage).
registerCardEffect('EB01-015', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    if (destroyedOwnerSide(card, ctx) !== 'player') return;
    const restedCount = state.playerField.filter(c => c && c.uid !== card.uid && c.rested).length
      + state.oppField.filter(c => c && c.uid !== card.uid && c.rested).length;
    if (restedCount < 2) {
      addLog(`${card.name} — fewer than 2 other rested Units in play. No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── EB01-017 · Haro ──────────────────────────────────────────────────────
// [Destroyed] If this Unit is destroyed with battle damage, you and the
// player who destroyed this Unit draw 1.
// Battle damage only (ctx.attacker present rules out the effect-damage
// destroy path). Owner draws locally; the destroyer draws via FORCE_DRAW,
// same "all players" convention as GD04-128 Armory One.
registerCardEffect('EB01-017', {
  destroyed: (card, ctx) => {
    if (!ctx || !ctx.attacker) return;
    if (destroyedOwnerSide(card, ctx) !== 'player') return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Destroyed] you draw ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Destroyed] deck is empty, no draw.`);
    }
    broadcastCounters();
    render();
    if (state.networked) NET.send({ type: 'FORCE_DRAW', count: 1 });
    ctx.onDone?.();
  },
});

// ─── EB01-005 · Zeta Gundam III P2 Type ───────────────────────────────────
// [Deploy] Choose 1 rested Unit belonging to another player. Set it as
// active. Draw 1.
registerCardEffect('EB01-005', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested enemy Unit. Set it as active.', candidates, (target) => {
      if (!target) return;
      setRested(target, false);
      addLog(`${card.name} — set ${target.name} active.`);
      const drawn = drawCardsFromTempDeck(1, true);
      if (drawn.length > 0) {
        state.hand.push(drawn[0]);
        addLog(`${card.name} — drew ${drawn[0].name}.`);
      }
      broadcastCounters();
      render();
      setupDropZones();
    }, card, { mandatory: true });
  },
});

// ─── ST10-001 · Zeta Gundam (EX) ──────────────────────────────────────────
// When this Unit destroys an enemy shield area card (Base or shield) with
// battle damage, set it as active. It can't choose the same enemy player or
// enemy team as its attack target during this turn.
// This engine is 1v1-only (a single opponent, no multi-team support), so
// "can't choose the same enemy player/team again" has no other enemy to
// redirect to — it's implemented as the practical 1v1 equivalent: this Unit
// can't attack again this turn (tempCantAttack).
// onAttackDestroysShield fires from runDamageStep for both a direct shield
// hit and a battle-damage base kill (comment there: "'Shield area card'
// includes the base") — same hook as ST03-001 Sinanju.
registerCardEffect('ST10-001', {
  onAttackDestroysShield: (card) => {
    setRested(card, false);
    card.tempCantAttack = true;
    addLog(`${card.name} — destroyed an enemy shield area card: set active (can't attack again this turn).`);
    render();
    setupDropZones();
  },
});

// ─── EB01-014 · Gouf Vijayanta ────────────────────────────────────────────
// During your opponent's turn, this Unit can't receive effect damage from
// enemy Units that are Lv.5 or lower.
// effectDamageImmune has no attacker-identity parameter — dealDamageToUnit()
// never threads a source Unit through — so the Lv.5-or-lower condition is
// approximated as blanket immunity to enemy effect damage during the
// opponent's turn, same convention as GD02-064 Gundam Leopard's "enemy
// Commands" approximation. Battle damage is unaffected (checked separately
// in runDamageStep, not dealDamageToUnit).
registerCardEffect('EB01-014', {
  effectDamageImmune: () => !state.myTurn,
});

// ─── EB01-013 · Red Gundam (0085) ─────────────────────────────────────────
// [Attack] If an enemy player has 6 or more cards in their hand, this Unit
// gets AP+2 during this turn.
registerCardEffect('EB01-013', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (state.oppHandCount < 6) {
      addLog(`${card.name} — opponent has fewer than 6 cards in hand. No effect.`);
      ctx.onDone?.(); return;
    }
    applyApMod([card], 2, 'tempApMod');
    addLog(`${card.name} — opponent has ${state.oppHandCount} cards in hand: gets AP+2 during this turn.`);
    render();
    ctx.onDone?.();
  },
});

// ─── EB01-019 · Gundam Pixy ────────────────────────────────────────────────
// [Attack] If there are 2 or more other rested Units in play, this Unit
// gains <High-Maneuver> during this battle.
// "During this battle" keyword grants are approximated as turn-scoped via
// tempKeywords (cleared by UNREST_ALL) — same convention as GD02-095
// Lafter Frankland.
registerCardEffect('EB01-019', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const restedCount = state.playerField.filter(c => c && c.uid !== card.uid && c.rested).length
      + state.oppField.filter(c => c && c.uid !== card.uid && c.rested).length;
    if (restedCount < 2) {
      addLog(`${card.name} — fewer than 2 other rested Units in play. No effect.`);
      ctx.onDone?.(); return;
    }
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('High-Maneuver')) card.tempKeywords.push('High-Maneuver');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'High-Maneuver' });
    addLog(`${card.name} — 2+ other rested Units in play: gains <High-Maneuver> this battle.`);
    render();
    ctx.onDone?.();
  },
});

// ─── GD03-086 · Yazan Gable ───────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Attack] Choose 1 of your (Titans) Units whose Lv. is equal to or lower
// than this Unit. It gets AP+1 during this turn.
registerCardEffect('GD03-086', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    const unitLv = parseInt(unit.level) || 0;
    const candidates = state.playerField.filter(c =>
      c && (c.trait || '').includes('(Titans)') && (parseInt(c.level) || 0) <= unitLv
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Titans) Units of Lv.${unitLv} or lower in play.`);
      ctx?.onDone?.(); return;
    }
    showCardPickerModal(
      card.name,
      `[Attack] Choose 1 of your (Titans) Units (Lv.${unitLv} or lower). It gets AP+1 this turn.`,
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        applyApMod([target], 1, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+1 this turn.`);
        render();
        setupDropZones();
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-087 · Sarah Zabiarov ────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] Choose 1 enemy Unit that is Lv.3 or lower. Rest it.
registerCardEffect('GD03-087', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    const candidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units Lv.3 or lower in play.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Linked] Choose 1 active enemy Unit that is Lv.3 or lower. Rest it.',
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-081 · Üso Ewin ─────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] If this is a (League Militaire) Unit, deploy 1 Parts (T-021) token.
registerCardEffect('GD04-081', {
  pair: (card, ctx) => {
    if (!(card.trait || '').includes('(League Militaire)')) return;
    const token = deployTokenToField('T-021');
    if (token) {
      addLog(`${card.name} — deployed Parts (T-021) token.`);
    } else {
      addLog(`${card.name} — no empty field slot for Parts token.`);
    }
    render();
    setupDropZones();
  },
});

// ─── GD04-082 · Rosamia Badam ─────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] Choose 1 rested enemy Unit. Deal 1 damage to it.
registerCardEffect('GD04-082', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Linked] Choose 1 rested enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-083 · Marbet Fingerhat ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// All your (League Militaire) Unit tokens get AP+1.
// Implemented via globalPilotApBonus — fires for every unit on the same side,
// returns +1 when the target is a (League Militaire) Unit token.
registerCardEffect('GD04-083', {
  globalPilotApBonus: (targetCard, pilot, sourceUnit, ownerSide) => {
    if (!isUnitToken(targetCard)) return 0;
    if (!(targetCard.trait || '').includes('(League Militaire)')) return 0;
    return 1;
  },
});

// ─── GD04-084 · Sleggar Law ───────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Attack] Choose 1 of your (White Base Team) Units. It gets AP+1 during this turn.
registerCardEffect('GD04-084', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(White Base Team)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no (White Base Team) Units in play.`);
      ctx?.onDone?.(); return;
    }
    showCardPickerModal(
      card.name,
      '[Attack] Choose 1 of your (White Base Team) Units. It gets AP+1 this turn.',
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        applyApMod([target], 1, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+1 this turn.`);
        render();
        setupDropZones();
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST01-010 · Amuro Ray ─────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Choose 1 enemy Unit with 5 or less HP. Rest it.
registerCardEffect('ST01-010', {
  pair: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with 5 or less HP.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Paired] Choose 1 active enemy Unit with 5 or less HP. Rest it.',
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST02-011 · Zechs Merquise ────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] During your turn, when this Unit destroys an enemy Unit with
// battle damage, draw 1.
// Uses onLinkedUnitDestroysEnemy — standing hook checked live off the
// destroyer's pilot in notifyUnitDestroyedByBattle(); see registry.js.
registerCardEffect('ST02-011', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (!isLinkUnit(unit)) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length === 0) {
      addLog(`${pilot.name} — deck is empty, no draw.`);
      return;
    }
    state.hand.push(drawn[0]);
    addLog(`${pilot.name} — ${unit.name} destroyed ${destroyedUnit.name} in battle. Drew ${drawn[0].name}.`);
    broadcastCounters();
    render();
  },
});

// ─── ST08-011 · Lane Aim ──────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// When you draw with an effect, if this is a blue Unit, it gains <High-Maneuver>
// during this turn.
// onEffectDraw fires from drawCardsFromTempDeck(count, true) calls.
registerCardEffect('ST08-011', {
  onEffectDraw: (pilot, unit) => {
    if ((unit.color || '').toLowerCase() !== 'blue') return;
    unit.tempKeywords = unit.tempKeywords || [];
    if (unit.tempKeywords.includes('High-Maneuver')) return;
    unit.tempKeywords.push('High-Maneuver');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'High-Maneuver' });
    addLog(`${pilot.name} — ${unit.name} gains <High-Maneuver> this turn.`);
    render();
  },
});

// ─── GD04-121 · Reineforce Jr. ───────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand (generic). Then, during your turn, if a
// friendly (League Militaire) Unit is in play, deploy 1 Parts (T-021) token.
registerCardEffect('GD04-121', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const hasLM = state.playerField.some(c => c && (c.trait || '').includes('(League Militaire)'));
    if (!hasLM) return;
    const token = deployTokenToField('T-021');
    if (token) {
      addLog(`${card.name} — deployed Parts token.`);
    } else {
      addLog(`${card.name} — no empty field slot for Parts token.`);
    }
    render();
    setupDropZones();
  },
});

// ─── GD04-122 · Jaburo ───────────────────────────────────────────────────────
// [Activate･Main][Once per Turn] Rest 1 of your (Earth Federation) Units:
// Choose 1 enemy Unit that is Lv.3 or lower. Rest it.
// Non-standard cost ("Rest 1 of your (Earth Federation) Units") is handled
// entirely inside the activate handler, same pattern as Trinity Warship.
registerCardEffect('GD04-122', {
  canActivate: () => {
    const hasActiveEF = state.playerField.some(u => u && !u.rested && (u.trait || '').includes('(Earth Federation)'));
    const hasEnemyTarget = state.oppField.some(c => c && !c.rested && (parseInt(c.level) || 0) <= 3);
    return hasActiveEF && hasEnemyTarget;
  },
  activate: (card, ctx) => {
    const efCandidates = state.playerField.filter(u => u && !u.rested && (u.trait || '').includes('(Earth Federation)'));
    if (efCandidates.length === 0) {
      addLog(`${card.name} — no active (Earth Federation) Units to rest.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your active (Earth Federation) Units. Rest it as the cost.',
      efCandidates,
      (efUnit) => {
        if (!efUnit) { ctx.onDone(); return; }
        setRested(efUnit, true);
        addLog(`${card.name} — rested ${efUnit.name} as cost.`);
        render();
        setupDropZones();
        const enemyCandidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 3);
        if (enemyCandidates.length === 0) {
          addLog(`${card.name} — no enemy Units Lv.3 or lower to target.`);
          ctx.onDone(); return;
        }
        showCardPickerModal(
          card.name,
          'Choose 1 enemy Unit that is Lv.3 or lower. Rest it.',
          enemyCandidates,
          (target) => {
            if (!target) { ctx.onDone(); return; }
            setRested(target, true);
            addLog(`${card.name} — ${target.name} rested.`);
            render();
            setupDropZones();
            ctx.onDone();
          },
          card,
          { mandatory: true }
        );
      },
      card,
      { mandatory: true }
    );
  },
});

// ══════════════════════════════════════════════════════════════════════════════
// ── UNIT EFFECTS ─────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

// ─── GD01-001 · Gundam (White Base Team Lv.4) ────────────────────────────────
// All your (White Base Team) Units gain <Repair 1>.
// [When Paired] If you have 2 or more other Units in play, draw 1.
//
// passiveKeywords grants this Unit its own Repair 1 badge. globalUnitKeyword
// propagates Repair 1 to other WBT units; getRepairValue checks this last, so
// native Repair-2 cards (e.g. ST01-001) are unaffected. ALTP1/ALTP2 identical.
function gd01001PairDraw(card) {
  if (state.playerField.filter(c => c && c.uid !== card.uid).length < 2) return;
  const drawn = drawCardsFromTempDeck(1, true);
  if (drawn.length > 0) {
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name} (2+ other Units in play).`);
  }
  broadcastCounters();
  render();
}
const gd01001Handlers = {
  passiveKeywords: () => ['Repair 1'],
  globalUnitKeyword: (targetCard, _src, ownerSide) =>
    ownerSide === 'player' && (targetCard.trait || '').includes('White Base Team')
      ? ['Repair 1'] : [],
  pair: (card) => gd01001PairDraw(card),
};
registerCardEffect('GD01-001',      gd01001Handlers);
registerCardEffect('GD01-001-ALTP1', gd01001Handlers);
registerCardEffect('GD01-001-ALTP2', gd01001Handlers);

// ─── GD01-002 · Unicorn Gundam (Destroy Mode) ────────────────────────────────
// When playing from hand, you may destroy 1 of your "Unicorn Mode" Link Units
// (Lv.5). If you do, play this card with 0 Lv. and cost.
// [Attack] Choose 1 enemy Unit. Rest it.
//
// prePlayFromHand intercepts the field-drop. If a sacrifice candidate exists
// the player is offered an optional picker. Setting _destroyModeSacrificed flag
// makes handLevelReduction/handCostReduction return the full reduction (level-1
// / full cost) so the card deploys for free. deploy clears the flag.
// ALTP1/ALTP2 identical.
const gd01002Handlers = {
  // Lets canPlayCard's hand-dimming check see the discounted level/cost
  // (matching handLevelReduction/handCostReduction below) when a valid
  // sacrifice candidate exists, so the card isn't shown dimmed when the
  // discount would make it playable — same fix as ST10-014.
  altCostPreview: (card) => {
    const hasCandidate = state.playerField.some(c =>
      c && isLinkUnit(c) && (c.name || '').includes('Unicorn Mode') && (parseInt(c.level) || 0) === 5
    );
    if (!hasCandidate) return null;
    return {
      level: Math.max(0, (parseInt(card.level) || 0) - 1),
      cost: 0,
    };
  },
  prePlayFromHand: (card, _slot, onProceed) => {
    const candidates = state.playerField.filter(c =>
      c && isLinkUnit(c) && (c.name || '').includes('Unicorn Mode') && (parseInt(c.level) || 0) === 5
    );
    if (candidates.length === 0) { onProceed(); return; }
    showCardPickerModal(
      card.name,
      'You may destroy 1 of your "Unicorn Mode" Link Units (Lv.5). If you do, play this card with 0 Lv. and cost.',
      candidates,
      (chosen) => {
        if (chosen) {
          card._destroyModeSacrificed = true;
          const sacSlot = state.playerField.findIndex(c => c && c.uid === chosen.uid);
          // destroy: true — this is a real destruction (the card text says
          // "destroy"), so it fires chosen's own [Destroyed] trigger (e.g.
          // GD01-005 Unicorn Gundam (Unicorn Mode)'s "[During Link][Destroyed]
          // Return this Unit's paired Pilot to its owner's hand. Then, discard
          // 1.") instead of silently trashing it like a plain discard.
          dispatch('DISCARD_CARD', { uid: chosen.uid, source: 'field', slot: sacSlot, destroy: true });
          addLog(`${card.name} — destroyed ${chosen.name} as cost.`);
          broadcastCounters(); render(); setupDropZones();
        }
        onProceed();
      },
      card
    );
  },
  handLevelReduction: (card) =>
    card._destroyModeSacrificed ? Math.max(0, (parseInt(card.level) || 0) - 1) : 0,
  handCostReduction: (card) =>
    card._destroyModeSacrificed ? (parseInt(card.cost) || 0) : 0,
  deploy: (card) => { delete card._destroyModeSacrificed; },
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && !c.rested);
    if (candidates.length === 0) { ctx.onDone?.(); return; }
    showCardPickerModal(
      card.name, 'Choose 1 active enemy Unit. Rest it.', candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${card.name} — ${target.name} rested.`);
          render(); setupDropZones();
        }
        ctx.onDone?.();
      },
      card, { mandatory: true }
    );
  },
};
registerCardEffect('GD01-002',      gd01002Handlers);
registerCardEffect('GD01-002-ALTP1', gd01002Handlers);
registerCardEffect('GD01-002-ALTP2', gd01002Handlers);

// ─── GD01-003 · Unicorn Gundam 02 Banshee (Destroy Mode) ─────────────────────
// [During Link][Attack] Choose 12 cards from your trash. Return them to your
// deck and shuffle it. If you do, set this Unit as active and gain <First Strike>.
// ALTP1 identical.
function gd01003AttackEffect(card, ctx) {
  if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
  if (!isLinkUnit(card)) { ctx.onDone?.(); return; }
  if (state.playerTrash.length < 12) {
    addLog(`${card.name} — need at least 12 cards in trash. No effect.`);
    ctx.onDone?.(); return;
  }
  showCardPickerModalMulti(
    card.name,
    'Choose exactly 12 cards from your trash. Return them to your deck and shuffle it. This Unit stays active and gains <First Strike> this turn.',
    state.playerTrash,
    { min: 12, max: 12, mandatory: true },
    (chosen) => {
      if (!chosen || chosen.length < 12) { ctx.onDone?.(); return; }
      chosen.forEach(c => {
        const idx = state.playerTrash.findIndex(t => t.uid === c.uid);
        if (idx !== -1) {
          state.playerTrash.splice(idx, 1);
          state.trashCount = Math.max(0, state.trashCount - 1);
          updateTempState(c.cardNo, c.copyVer, 'deck');
        }
      });
      shuffleTempDeck();
      // "Set this Unit as active" while it's mid-attack means it must never
      // rest for this attack at all — the normal per-attack rest happens
      // later, after Block/Action Steps, in restAttackerThenDamage — so this
      // just arms canStayActiveAfterAttack below instead of calling
      // setRested() directly here (which restAttackerThenDamage would have
      // unconditionally overwritten moments later, re-resting the Unit and
      // silently undoing this effect — see the bug this fixes).
      card._banshee12ReturnedThisAttack = true;
      card.tempKeywords = card.tempKeywords || [];
      card.tempKeywords.push('First Strike');
      NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'First Strike' });
      addLog(`${card.name} — returned 12 cards to deck, shuffled. Stays active with <First Strike> this turn.`);
      broadcastCounters(); render(); setupDropZones();
      ctx.onDone?.();
    },
    card
  );
}
const gd01003Handlers = {
  attack: gd01003AttackEffect,
  // Read-and-clear: only the attack that actually resolved the 12-card
  // return should skip resting, not a later unrelated attack this same turn.
  canStayActiveAfterAttack: (card) => {
    const result = !!card._banshee12ReturnedThisAttack;
    card._banshee12ReturnedThisAttack = false;
    return result;
  },
};
registerCardEffect('GD01-003',      gd01003Handlers);
registerCardEffect('GD01-003-ALTP1', gd01003Handlers);

// ─── GD01-004 · Guncannon (White Base Team) ──────────────────────────────────
// <Repair 1>
// [When Paired] Choose 1 enemy Unit with 2 or less HP. Rest it.
// ALTP1/ALTP2 identical.
function gd01004PairEffect(card) {
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 2 or less HP.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 active enemy Unit with 2 or less HP. Rest it.', candidates,
    (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD01-004',      { pair: gd01004PairEffect });
registerCardEffect('GD01-004-ALTP1', { pair: gd01004PairEffect });
registerCardEffect('GD01-004-ALTP2', { pair: gd01004PairEffect });

// ─── GD01-005 · Unicorn Gundam (Unicorn Mode) ────────────────────────────────
// [During Link][Destroyed] Return this Unit's paired Pilot to its owner's hand.
// Then, discard 1.
// ALTP1/2: [During Pair][Destroyed]If this is a Link Unit, ...
// ALTP3:   [During Link][Destroyed]If this is a Link Unit, ...
// All variants share the same logic: isLinkUnit check + rescuePilot + discard 1.
function unicornModeDestroyedEffect(card, ctx) {
  if (!ctx) return;
  if (!isLinkUnit(card)) return;
  // destroyedOwnerSide handles both the battle-destroy ctx shape (defender/
  // attackerSide, no ownerSide field) and the plain-ownerSide shape used by
  // non-battle destructions like GD01-002's alt-cost sacrifice or effect
  // damage — the old inline defender/attackerSide-only check resolved to
  // undefined (and silently no-opped) for those.
  const ownerSide = destroyedOwnerSide(card, ctx);
  if (ownerSide !== 'player') return;
  if (!card.pilot) return;
  ctx.rescuePilot?.();
  const pilot = card.pilot;
  state.hand.push(pilot);
  updateTempState(pilot.cardNo, pilot.copyVer, 'hand');
  addLog(`${card.name} — ${pilot.name} returned to hand.`);
  broadcastCounters(); render();
  if (state.hand.length === 0) return;
  showCardPickerModal(
    card.name, 'Discard 1 card from your hand.',
    state.hand,
    (discarded) => {
      if (!discarded) { broadcastCounters(); render(); return; }
      state.hand = state.hand.filter(c => c.uid !== discarded.uid);
      updateTempState(discarded.cardNo, discarded.copyVer, 'trash');
      state.trashCount++;
      state.playerTrash.push(toTrashCard(discarded));
      broadcastLog(`${card.name} — discarded ${discarded.name}.`);
      broadcastCardToast(`${discarded.name} was discarded due to ${card.name} effect.`);
      broadcastCounters(); render();
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD01-005',      { destroyed: unicornModeDestroyedEffect });
registerCardEffect('GD01-005-ALTP1', { destroyed: unicornModeDestroyedEffect });
registerCardEffect('GD01-005-ALTP2', { destroyed: unicornModeDestroyedEffect });
registerCardEffect('GD01-005-ALTP3', { destroyed: unicornModeDestroyedEffect });

// ─── GD01-006 · Delta Plus ───────────────────────────────────────────────────
// <Repair 1>
// [During Link] This Unit gets HP+1.
// ALTP1 identical.
const gd01006Handlers = {
  passiveUnitHpBonus: (card) => isLinkUnit(card) ? 1 : 0,
};
registerCardEffect('GD01-006',      gd01006Handlers);
registerCardEffect('GD01-006-ALTP1', gd01006Handlers);

// ─── GD01-007 · Noin's Aries ─────────────────────────────────────────────────
// [Destroyed] If you have another (OZ) Unit in play, draw 1.
registerCardEffect('GD01-007', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (ownerSide !== 'player') return;
    if (!state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(OZ)'))) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Destroyed] drew ${drawn[0].name} (another OZ Unit in play).`);
    }
    broadcastCounters(); render();
  },
});

// ─── GD01-008 · Guntank (White Base Team) ────────────────────────────────────
// [Deploy] Choose 1 rested enemy Unit. Deal 1 damage to it.
// ALTP1/ALTP2 identical.
function gd01008DeployEffect(card) {
  const candidates = state.oppField.filter(c => c && c.rested);
  if (candidates.length === 0) {
    addLog(`${card.name} — no rested enemy Units.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 rested enemy Unit. Deal 1 damage to it.', candidates,
    (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD01-008',      { deploy: gd01008DeployEffect });
registerCardEffect('GD01-008-ALTP1', { deploy: gd01008DeployEffect });
registerCardEffect('GD01-008-ALTP2', { deploy: gd01008DeployEffect });

// ─── EB01-002 · Hi-Nu Gundam (EX) ────────────────────────────────────────────
// [Deploy] If another friendly (G Generation) Unit is in play, choose 1 Unit
// belonging to each enemy player. Rest them. (1v1: just the single opponent.)
// [During Link][Attack][Once per Turn] If 3 or more OTHER rested Units are
// in play, set this Unit as active.
// "Set this Unit as active" here means the attack doesn't rest it at all —
// implemented via the canStayActiveAfterAttack hook (checked in game.js at
// both attacker-resting points, since the mine=false/defender path rests
// the attacker before any trigger can run). "Other" excludes this card
// itself, per raw card text, even though it's about to rest to attack.
registerCardEffect('EB01-002', {
  deploy: (card) => {
    const hasOtherGGen = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(G Generation)'));
    if (!hasOtherGGen) return;
    const candidates = state.oppField.filter(c => c && !c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units in play. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 active enemy Unit. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
  canStayActiveAfterAttack: (card) => {
    if (!isLinkUnit(card)) return false;
    if (card.passiveUsedThisTurn) return false;
    const restedOtherCount = state.playerField.filter(c => c && c.uid !== card.uid && c.rested).length
      + state.oppField.filter(c => c && c.uid !== card.uid && c.rested).length;
    if (restedOtherCount < 3) return false;
    card.passiveUsedThisTurn = true;
    addLog(`${card.name} — 3+ other rested Units in play: stays active after attacking.`);
    return true;
  },
});

// ─── EB01-003 · Narrative Gundam A-Packs (EX) ────────────────────────────────
// <Repair 2> (native, handled generically by getRepairValue/REPAIR_FIELD).
// At the end of your turn, if this Unit is rested, rest all Units. If this
// effect rested 3 or more Units, draw 1.
// "This effect rested" — only newly-active-to-rested units count, not ones
// already rested beforehand.
registerCardEffect('EB01-003', {
  endOfTurn: (card, onDone) => {
    if (!card.rested) { onDone(); return; }
    const allUnits = [...state.playerField, ...state.oppField].filter(c => c);
    let newlyRestedCount = 0;
    allUnits.forEach(u => {
      if (!u.rested) {
        setRested(u, true);
        newlyRestedCount++;
      }
    });
    addLog(`${card.name} — rested; end of turn: rested all Units (${newlyRestedCount} newly rested).`);
    if (newlyRestedCount >= 3) {
      const drawn = drawCardsFromTempDeck(1, true);
      if (drawn.length > 0) {
        state.hand.push(drawn[0]);
        addLog(`${card.name} — rested 3+ Units: drew ${drawn[0].name}.`);
      }
      broadcastCounters();
    }
    render();
    setupDropZones();
    onDone();
  },
});

// ─── ST10-002 · Zeta Gundam ───────────────────────────────────────────────────
// [Deploy･Development 2] You may exile 2 (G Generation) Unit cards from your
// trash. If you do: Choose 1 enemy Unit with 4 or less HP. Rest it.
registerCardEffect('ST10-002', {
  deploy: (card) => {
    // Check the sub-effect has a valid target BEFORE offering the exile
    // cost at all — otherwise "you may exile..." could pay the cost for a
    // follow-up effect that was never going to do anything (no active
    // Lv.4-or-lower enemy Unit to rest).
    if (!state.oppField.some(c => c && !c.rested && effectiveHp(c) <= 4)) return;
    resolveDevelopmentCost(card, 2, () => {
      const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 4);
      if (candidates.length === 0) {
        addLog(`${card.name} — no active enemy Units with 4 or less HP. No effect.`);
        return;
      }
      showCardPickerModal(card.name, 'Choose 1 active enemy Unit with 4 or less HP. Rest it.', candidates, (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${card.name} — rested ${target.name}.`);
          render();
          setupDropZones();
        }
      }, card, { mandatory: true });
    });
  },
});

// ─── EB01-009 · Gundam Full Armor (Thunderbolt) (EX) ─────────────────────────
// [Deploy] All enemy players each choose 1 of their active Units. Rest them.
// Enemy only (confirmed) — this engine has no opponent-choice networking,
// so the activating player picks the enemy target, same convention as
// EB01-074 Eternal Road.
registerCardEffect('EB01-009', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && !c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 active enemy Unit. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── EB01-010 · Gundam Barbatos 6th Form ─────────────────────────────────────
// [Deploy･Development 3] You may exile 3 (G Generation) Unit cards from your
// trash. If you do: Choose 1 rested enemy Unit. Deal 2 damage to it.
// resolveDevelopmentCost is defined in green.js but globally accessible.
registerCardEffect('EB01-010', {
  deploy: (card) => {
    resolveDevelopmentCost(card, 3, () => {
      const candidates = state.oppField.filter(c => c && c.rested);
      if (candidates.length === 0) {
        addLog(`${card.name} — no rested enemy Units. No effect.`);
        return;
      }
      showCardPickerModal(card.name, 'Choose 1 rested enemy Unit. Deal 2 damage to it.', candidates, (target) => {
        if (target) {
          dealDamageToUnit(target, 2, { fromEnemy: true });
          addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
          render();
          setupDropZones();
        }
      }, card, { mandatory: true });
    });
  },
});

// ─── EB01-008 · Gundam Delta Kai ──────────────────────────────────────────────
// [Deploy･Development 1] You may exile 1 (G Generation) Unit card from your
// trash. If you do: Choose 1 friendly Unit. It recovers 2 HP.
registerCardEffect('EB01-008', {
  deploy: (card) => {
    resolveDevelopmentCost(card, 1, () => {
      const candidates = state.playerField.filter(c => c);
      if (candidates.length === 0) {
        addLog(`${card.name} — no friendly Units in play. No effect.`);
        return;
      }
      showCardPickerModal(card.name, 'Choose 1 friendly Unit. It recovers 2 HP.', candidates, (target) => {
        if (target) {
          applyHpMod([target], 2);
          addLog(`${card.name} — ${target.name} recovers 2 HP (now: ${effectiveHp(target)}).`);
          render();
          setupDropZones();
        }
      }, card, { mandatory: true });
    });
  },
});

// ─── EB01-006 · Gundam Astray Gold Frame Amatsu ──────────────────────────────
// [Deploy] Choose 1 of your Units. It gains <Repair 1> during this turn.
registerCardEffect('EB01-006', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 of your Units. It gains <Repair 1> during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Repair 1')) target.tempKeywords.push('Repair 1');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Repair 1' });
        addLog(`${card.name} — ${target.name} gains <Repair 1> this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-009 · G-Fighter ────────────────────────────────────────────────────
// [Deploy] Choose 1 of your (White Base Team) Units. It gains <High-Maneuver>
// during this turn.
// ALTP1 identical.
function gd01009DeployEffect(card) {
  const candidates = state.playerField.filter(c => c && (c.trait || '').includes('White Base Team'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no (White Base Team) Units in play.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 of your (White Base Team) Units. It gains <High-Maneuver> this turn.', candidates,
    (target) => {
      if (target) {
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('High-Maneuver');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'High-Maneuver' });
        addLog(`${card.name} — ${target.name} gains <High-Maneuver> this turn.`);
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD01-009',      { deploy: gd01009DeployEffect });
registerCardEffect('GD01-009-ALTP1', { deploy: gd01009DeployEffect });

// ─── GD01-010 · Unicorn Gundam 02 Banshee (Unicorn Mode) ─────────────────────
// [When Paired] Choose 1 enemy Unit with 3 or less HP. Rest it.
function gd01010PairEffect(card) {
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 3 or less HP.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 active enemy Unit with 3 or less HP. Rest it.', candidates,
    (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD01-010', { pair: gd01010PairEffect });

// ─── GD01-012 · Zechs' Leo ───────────────────────────────────────────────────
// [When Paired] Choose 1 enemy Unit with 3 or less HP. Rest it.
registerCardEffect('GD01-012', { pair: gd01010PairEffect });

// ─── GD01-014 · G-Sky Easy ───────────────────────────────────────────────────
// [During Link][Activate･Action][Once per Turn] Choose 1 Unit. It recovers 1 HP.
const gd01014Handlers = {
  canAction: (card) => isLinkUnit(card),
  action: (card, ctx) => {
    const allUnits = [
      ...state.playerField.filter(c => c),
      ...state.oppField.filter(c => c),
    ];
    if (allUnits.length === 0) {
      addLog(`${card.name} — no Units in play.`);
      ctx.onDone?.(); return true;
    }
    showCardPickerModal(
      card.name, 'Choose 1 Unit. It recovers 1 HP.', allUnits,
      (target) => {
        if (target) {
          applyHpMod([target], 1);
          addLog(`${card.name} — ${target.name} recovers 1 HP (now: ${effectiveHp(target)}).`);
          render(); setupDropZones();
        }
        ctx.onDone?.();
      },
      card, { mandatory: true }
    );
    return true;
  },
};
registerCardEffect('GD01-014', gd01014Handlers);

// ─── EB01-020 · Gundam Mk-III ─────────────────────────────────────────────
// [During Link][Activate･Action][Once per Turn] Choose 1 Unit. It recovers 1 HP.
// Identical effect to GD01-014 G-Sky Easy — reuses its handlers.
registerCardEffect('EB01-020', gd01014Handlers);

// ─── GD01-015 · Ball ─────────────────────────────────────────────────────────
// [Attack] Choose 1 of your Units. It recovers 1 HP.
// ALTP1 identical.
function gd01015AttackEffect(card, ctx) {
  if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
  const candidates = state.playerField.filter(c => c);
  if (candidates.length === 0) { ctx.onDone?.(); return; }
  showCardPickerModal(
    card.name, 'Choose 1 of your Units. It recovers 1 HP.', candidates,
    (target) => {
      if (target) {
        applyHpMod([target], 1);
        addLog(`${card.name} — ${target.name} recovers 1 HP (now: ${effectiveHp(target)}).`);
        render(); setupDropZones();
      }
      ctx.onDone?.();
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD01-015',      { attack: gd01015AttackEffect });
registerCardEffect('GD01-015-ALTP1', { attack: gd01015AttackEffect });

// ─── EB01-018 · Gundam Astray Blue Frame Second L ────────────────────────────
// [Attack] Choose 1 friendly Unit. It recovers 1 HP.
// Identical effect to GD01-015 Ball — reuses its attack handler.
registerCardEffect('EB01-018', { attack: gd01015AttackEffect });

// ─── GD01-016 · Jegan ────────────────────────────────────────────────────────
// While you have 2 or more (Earth Federation) Units in play, this card in your
// hand gets cost -1. ALTP1/ALTP2/ALTP3 identical.
function jegan16CostReduction() {
  return state.playerField.filter(c => c && (c.trait || '').includes('Earth Federation')).length >= 2 ? 1 : 0;
}
const gd01016Handlers = { handCostReduction: jegan16CostReduction };
registerCardEffect('GD01-016',      gd01016Handlers);
registerCardEffect('GD01-016-ALTP1', gd01016Handlers);
registerCardEffect('GD01-016-ALTP2', gd01016Handlers);
registerCardEffect('GD01-016-ALTP3', gd01016Handlers);

// ─── GD01-019 · Byarlant Custom ──────────────────────────────────────────────
// While 4 or more enemy Units are in play, this Unit gains <Blocker>.
registerCardEffect('GD01-019', {
  passiveKeywords: () =>
    state.oppField.filter(c => c).length >= 4 ? ['Blocker'] : [],
});

// ─── GD01-020 · Anksha ───────────────────────────────────────────────────────
// [Deploy] Choose 1 rested enemy Unit. Deal 1 damage to it.
registerCardEffect('GD01-020', { deploy: gd01008DeployEffect });

// ─── GD02-001 · Psycho Gundam ────────────────────────────────────────────────
// <Breach 3>
// [During Pair·(Cyber-Newtype) Pilot] When one of your (Titans) Units destroys
// an enemy shield area card with damage, this Unit recovers 2 HP.
// ALTP1 identical.
function gd02001ShieldHook(card, attacker) {
  if (!card.pilot) return;
  if (!(card.pilot.trait || '').includes('Cyber-Newtype')) return;
  if (!(attacker.trait || '').includes('Titans')) return;
  applyHpMod([card], 2);
  addLog(`${card.name} — recovered 2 HP (Titans Unit destroyed enemy shield).`);
  render();
}
const gd02001Handlers = {
  onAnyPlayerAttackDestroysShield: gd02001ShieldHook,
};
registerCardEffect('GD02-001',      gd02001Handlers);
registerCardEffect('GD02-001-ALTP1', gd02001Handlers);

// ─── GD02-002 · Gundam Epyon ─────────────────────────────────────────────────
// [During Link][Once per Turn] During your turn, when one of your Units destroys
// an enemy Unit with battle damage, set this Unit as active.
// ALTP1 identical.
function gd02002KillHook(card, _destroyer, _destroyed, destroyerSide, destroyerIsAttacker) {
  if (card.passiveUsedThisTurn) return;
  if (!isLinkUnit(card)) return;
  if (!state.myTurn) return;
  if (destroyerSide !== 'player') return;
  if (!destroyerIsAttacker) return;
  card.passiveUsedThisTurn = true;
  setRested(card, false);
  addLog(`${card.name} — [During Link] set active (friendly Unit destroyed enemy).`);
  render(); setupDropZones();
}
const gd02002Handlers = {
  onAnyFriendlyUnitDestroysEnemy: gd02002KillHook,
  onUnitDestroysEnemy: (card, _destroyed, destroyerSide, destroyerIsAttacker) =>
    gd02002KillHook(card, card, _destroyed, destroyerSide, destroyerIsAttacker),
};
registerCardEffect('GD02-002',      gd02002Handlers);
registerCardEffect('GD02-002-ALTP1', gd02002Handlers);

// ─── GD02-003 · Gundam Mk-II (Titans) ────────────────────────────────────────
// [During Pair·Lv.3 or Lower Pilot][Destroyed] You may discard 1 Unit card.
// If you do, return the card paired with this Unit to your hand.
// ALTP1/ALTP2/ALTP3 identical.
function gd02003DestroyedEffect(card, ctx) {
  if (!ctx) return;
  if (!card.pilot) return;
  if ((parseInt(card.pilot.level) || 0) > 3) return;
  const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
  if (ownerSide !== 'player') return;
  const unitCandidates = state.hand.filter(c => c.type === 'unit');
  if (unitCandidates.length === 0) return;
  showCardPickerModal(
    card.name,
    'You may discard 1 Unit card from your hand to return the paired Pilot to your hand.',
    unitCandidates,
    (discarded) => {
      if (!discarded) return;
      state.hand = state.hand.filter(c => c.uid !== discarded.uid);
      updateTempState(discarded.cardNo, discarded.copyVer, 'trash');
      state.trashCount++;
      state.playerTrash.push(toTrashCard(discarded));
      broadcastLog(`${card.name} — discarded ${discarded.name}.`);
      broadcastCardToast(`${discarded.name} was discarded due to ${card.name} effect.`);
      ctx.rescuePilot?.();
      const pilot = card.pilot;
      state.hand.push(pilot);
      updateTempState(pilot.cardNo, pilot.copyVer, 'hand');
      addLog(`${card.name} — ${pilot.name} returned to hand.`);
      broadcastCounters(); render();
    },
    card
  );
}
registerCardEffect('GD02-003',      { destroyed: gd02003DestroyedEffect });
registerCardEffect('GD02-003-ALTP1', { destroyed: gd02003DestroyedEffect });
registerCardEffect('GD02-003-ALTP2', { destroyed: gd02003DestroyedEffect });
registerCardEffect('GD02-003-ALTP3', { destroyed: gd02003DestroyedEffect });

// ─── GD02-004 · Byarlant ─────────────────────────────────────────────────────
// [When Paired] Choose 1 rested enemy Unit with 3 or less HP. It won't be set
// as active during the start phase of your opponent's next turn.
registerCardEffect('GD02-004', {
  pair: (card) => {
    const candidates = state.oppField.filter(c => c && c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units with 3 or less HP.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 rested enemy Unit (3 or less HP). It won\'t unrest at start of opponent\'s next turn.',
      candidates,
      (target) => {
        if (target) {
          target.keepRestedNextTurn = true;
          NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'keepRestedNextTurn' });
          addLog(`${card.name} — ${target.name} won't unrest next turn.`);
          render();
        }
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD02-005 · Tallgeese (OZ Lv.4) ─────────────────────────────────────────
// [During Link][Attack] Choose 1 enemy Unit with 2 or less HP. Rest it.
// ALTP1 identical.
function gd02005AttackEffect(card, ctx) {
  if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
  if (!isLinkUnit(card)) { ctx.onDone?.(); return; }
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 2 or less HP.`);
    ctx.onDone?.(); return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 active enemy Unit with 2 or less HP. Rest it.', candidates,
    (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render(); setupDropZones();
      }
      ctx.onDone?.();
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD02-005',      { attack: gd02005AttackEffect });
registerCardEffect('GD02-005-ALTP1', { attack: gd02005AttackEffect });

// ─── GD02-006 · Forbidden Gundam ─────────────────────────────────────────────
// <Blocker>
// During your turn, this Unit can't receive battle damage from enemy Units
// that are Lv.2 or lower.
// ALTP1/ALTP2 identical.
const gd02006Handlers = {
  passiveUnitAttackerImmuneFromDefender: (unit, defender, attackerSide) =>
    state.myTurn && attackerSide === 'player' && (parseInt(defender.level) || 0) <= 2,
};
registerCardEffect('GD02-006',      gd02006Handlers);
registerCardEffect('GD02-006-ALTP1', gd02006Handlers);
registerCardEffect('GD02-006-ALTP2', gd02006Handlers);

// ─── GD02-008 · Gabthley ─────────────────────────────────────────────────────
// [When Linked] Choose 1 rested enemy Unit. Deal 1 damage to it.
registerCardEffect('GD02-008', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units.`);
      return;
    }
    showCardPickerModal(
      card.name, 'Choose 1 rested enemy Unit. Deal 1 damage to it.', candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 1, { fromEnemy: true });
          render(); setupDropZones();
        }
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD02-009 · Calamity Gundam ──────────────────────────────────────────────
// [Once per Turn] When this Unit's AP is reduced by an enemy effect, choose 1
// rested enemy Unit. Deal 2 damage to it.
registerCardEffect('GD02-009', {
  onEnemyApReduced: (card) => {
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — AP reduced! No rested enemy Units to target.`);
      return;
    }
    showCardPickerModal(
      card.name, 'Your AP was reduced. Choose 1 rested enemy Unit. Deal 2 damage to it.', candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 2, { fromEnemy: true });
          render(); setupDropZones();
        }
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD02-010 · Raider Gundam ─────────────────────────────────────────────────
// [Once per Turn] When this Unit receives enemy effect damage, draw 1.
registerCardEffect('GD02-010', {
  onSelfTakesEffectDamage: (card, fromEnemy) => {
    if (!fromEnemy) return;
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — received effect damage, drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
  },
});

// ─── GD02-011 · Moebius (Peacemaker Team) ────────────────────────────────────
// [Activate･Action] Destroy this Unit: Choose 1 enemy Base/enemy Shield this
// Unit is battling. Deal 6 damage to it.
//
// canAction gates eligibility to attacks against Base or Shield only.
// Self-destruction via dispatch('DISCARD_CARD') mirrors the red.js GD02-047
// pattern. Base damage uses dealDamageToBase; shield case logs manual resolve.
registerCardEffect('GD02-011', {
  canAction: (_card, ctx) => !!(ctx && (ctx.isBase || ctx.isShield)),
  action: (card, ctx) => {
    const battleCtx = ctx.battle;
    const slot = state.playerField.findIndex(c => c && c.uid === card.uid);
    if (slot === -1) { ctx.onDone?.(); return true; }
    dispatch('DISCARD_CARD', { uid: card.uid, source: 'field', slot });
    if (battleCtx?.isBase) {
      dealDamageToBase('opp', 6, { fromEnemy: true });
      addLog(`${card.name} — self-destroyed, dealt 6 damage to enemy base.`);
    } else {
      addLog(`${card.name} — self-destroyed. Deal 6 damage to the enemy shield being attacked manually.`);
    }
    broadcastCounters(); render(); setupDropZones();
    ctx.onDone?.();
    return true;
  },
});

// ─── GD02-014 · Galbaldy Beta ────────────────────────────────────────────────
// [Deploy] Choose 1 of your (Titans) Units. It gets AP+1 during this turn.
function titanDeployApBonus(card) {
  const candidates = state.playerField.filter(c => c && (c.trait || '').includes('Titans'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no (Titans) Units in play.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 of your (Titans) Units. It gets AP+1 this turn.', candidates,
    (target) => {
      if (target) {
        applyApMod([target], 1, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+1 this turn.`);
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD02-014', { deploy: titanDeployApBonus });

// ─── GD02-016 · Barzam ───────────────────────────────────────────────────────
// [Deploy] Choose 1 of your (Titans) Units. It gets AP+1 during this turn.
registerCardEffect('GD02-016', { deploy: titanDeployApBonus });

// ─── GD02-018 · Taurus ───────────────────────────────────────────────────────
// This Unit can't choose the enemy player as its attack target.
registerCardEffect('GD02-018', { cantAttackNonUnit: () => true });

// ─── GD03-001 · Gundam NT-1 ──────────────────────────────────────────────────
// <Repair 2>
// [When Paired] Choose 1 rested enemy Unit. Deal 1 damage to it. When this
// effect destroys an enemy Unit, draw 1.
// ALTP1 identical.
function gd03001PairEffect(card) {
  const candidates = state.oppField.filter(c => c && c.rested);
  if (candidates.length === 0) {
    addLog(`${card.name} — no rested enemy Units.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 rested enemy Unit. Deal 1 damage to it.', candidates,
    (target) => {
      if (!target) return;
      const targetUid = target.uid;
      dealDamageToUnit(target, 1, { fromEnemy: true });
      render(); setupDropZones();
      if (!state.oppField.find(c => c && c.uid === targetUid)) {
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length > 0) {
          state.hand.push(drawn[0]);
          addLog(`${card.name} — destroyed enemy Unit, drew ${drawn[0].name}.`);
          broadcastCounters(); render();
        }
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD03-001',      { pair: gd03001PairEffect });
registerCardEffect('GD03-001-ALTP1', { pair: gd03001PairEffect });

// ─── GD03-002 · The-O ────────────────────────────────────────────────────────
// <Repair 3>
// [During Pair] When one of your other Units with <Repair> attacks, choose 1
// enemy Unit whose Lv. is equal to or lower than that Unit. Rest it.
// ALTP1 identical.
function gd03002FriendlyAttackHook(card, attacker) {
  if (!card.pilot) return; // [During Pair]
  if (getRepairValue(attacker) <= 0) return;
  const attackerLevel = parseInt(attacker.level) || 0;
  const candidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= attackerLevel);
  if (candidates.length === 0) return;
  showCardPickerModal(
    card.name,
    `${attacker.name} attacked (has Repair). Choose 1 enemy Unit Lv.${attackerLevel} or lower. Rest it.`,
    candidates,
    (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — [During Pair] ${target.name} rested (friendly Repair Unit attacked).`);
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD03-002',      { onFriendlyUnitAttacks: gd03002FriendlyAttackHook });
registerCardEffect('GD03-002-ALTP1', { onFriendlyUnitAttacks: gd03002FriendlyAttackHook });

// ─── GD03-004 · Hambrabi ─────────────────────────────────────────────────────
// [Attack] If you have 2 or more other (Titans) Units in play, choose 1 enemy
// Unit with 5 or less HP. Rest it.
registerCardEffect('GD03-004', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const otherTitans = state.playerField.filter(c =>
      c && c.uid !== card.uid && (c.trait || '').includes('Titans')
    );
    if (otherTitans.length < 2) {
      addLog(`${card.name} — need 2+ other (Titans) Units in play.`);
      ctx.onDone?.(); return;
    }
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with 5 or less HP.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(
      card.name, 'Choose 1 active enemy Unit with 5 or less HP. Rest it.', candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${card.name} — ${target.name} rested.`);
          render(); setupDropZones();
        }
        ctx.onDone?.();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD03-005 · Kshatriya Besserung ─────────────────────────────────────────
// <Repair 1>
// [Deploy] Draw 1.
registerCardEffect('GD03-005', {
  deploy: (card) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Deploy] drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
  },
});

// ─── GD03-006 · Penelope (Module Form) ───────────────────────────────────────
// [Deploy] Choose 1 to 2 enemy Units with 3 or less HP. Rest them.
registerCardEffect('GD03-006', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with 3 or less HP.`);
      return;
    }
    showCardPickerModalMulti(
      card.name, 'Choose 1 to 2 active enemy Units with 3 or less HP. Rest them.', candidates,
      { min: 1, max: 2, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length === 0) return;
        chosen.forEach(target => {
          setRested(target, true);
          addLog(`${card.name} — ${target.name} rested.`);
        });
        render(); setupDropZones();
      },
      card
    );
  },
});

// ─── GD03-007 · Gundam NT-1 Full Armor ───────────────────────────────────────
// [Destroyed] Choose 1 enemy Unit with 3 or less HP. Rest it.
registerCardEffect('GD03-007', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (ownerSide !== 'player') return;
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with 3 or less HP.`);
      return;
    }
    showCardPickerModal(
      card.name, 'Choose 1 active enemy Unit with 3 or less HP. Rest it.', candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${card.name} — ${target.name} rested.`);
          render(); setupDropZones();
        }
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD03-008 · Bolinoak Sammahn ─────────────────────────────────────────────
// [During Pair] This Unit gains <Repair 2>.
registerCardEffect('GD03-008', {
  passiveKeywords: (card) => card.pilot ? ['Repair 2'] : [],
  conditionalRepair: (card) => card.pilot ? 2 : 0,
});

// ─── GD03-009 · Palace Athene ────────────────────────────────────────────────
// [Deploy] You may choose 2 (Titans) cards from your trash. Exile them from the
// game. If you do, choose 1 enemy Unit that is Lv.4 or lower. Rest it.
registerCardEffect('GD03-009', {
  deploy: (card) => {
    const titansCandidates = state.playerTrash.filter(c => (c.trait || '').includes('Titans'));
    if (titansCandidates.length < 2) return;
    // Check the sub-effect has a valid (active) target BEFORE offering the
    // exile choice — otherwise the optional cost could be paid for nothing.
    if (!state.oppField.some(c => c && !c.rested && (parseInt(c.level) || 0) <= 4)) return;
    showCardPickerModalMulti(
      card.name,
      'You may choose exactly 2 (Titans) cards from your trash to exile. If you do, rest 1 enemy Unit (Lv.4 or lower).',
      titansCandidates,
      { min: 2, max: 2, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length < 2) return;
        chosen.forEach(c => exileFromTrash(c));
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        const enemyCandidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 4);
        if (enemyCandidates.length === 0) {
          addLog(`${card.name} — no active enemy Units Lv.4 or lower to rest.`);
          broadcastCounters(); render(); setupDropZones(); return;
        }
        showCardPickerModal(
          card.name, 'Choose 1 active enemy Unit that is Lv.4 or lower. Rest it.', enemyCandidates,
          (target) => {
            if (target) {
              setRested(target, true);
              addLog(`${card.name} — ${target.name} rested.`);
            }
            broadcastCounters(); render(); setupDropZones();
          },
          card, { mandatory: true }
        );
      },
      card
    );
  },
});

// ─── GD03-013 · Hizack (Jupitris) ────────────────────────────────────────────
// While you have another (Jupitris) Unit in play, this Unit gets AP+1 and
// <Repair 1>.
registerCardEffect('GD03-013', {
  passiveUnitApBonus: (card) => {
    return state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('Jupitris')) ? 1 : 0;
  },
  passiveKeywords: (card) => {
    return state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('Jupitris')) ? ['Repair 1'] : [];
  },
  conditionalRepair: (card) => {
    return state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('Jupitris')) ? 1 : 0;
  },
});

// ─── GD03-014 · Hizack Custom ────────────────────────────────────────────────
// While you have 2 or more (Titans) Units in play, this card in your hand gets
// cost -1.
registerCardEffect('GD03-014', {
  handCostReduction: () =>
    state.playerField.filter(c => c && (c.trait || '').includes('Titans')).length >= 2 ? 1 : 0,
});

// ─── GD03-015 · Baund Doc ────────────────────────────────────────────────────
// [Activate･Main][Once per Turn] Exile 3 (Titans) cards from your trash:
// This Unit gains <Breach 4> during this turn.
registerCardEffect('GD03-015', {
  canActivate: (card) =>
    !card.activateMainUsedThisTurn &&
    state.playerTrash.filter(c => (c.trait || '').includes('Titans')).length >= 3,
  activate: (card, ctx) => {
    card.activateMainUsedThisTurn = true;
    const candidates = state.playerTrash.filter(c => (c.trait || '').includes('Titans'));
    if (candidates.length < 3) { card.activateMainUsedThisTurn = false; ctx.onDone(); return; }
    showCardPickerModalMulti(
      card.name,
      'Choose exactly 3 (Titans) cards from your trash to exile as cost. This Unit gains <Breach 4> this turn.',
      candidates,
      { min: 3, max: 3, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 3) {
          card.activateMainUsedThisTurn = false;
          ctx.onDone(); return;
        }
        chosen.forEach(c => exileFromTrash(c));
        card.tempKeywords = card.tempKeywords || [];
        card.tempKeywords.push('Breach 4');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'Breach 4' });
        addLog(`${card.name} — exiled 3 (Titans) cards, gains <Breach 4> this turn.`);
        broadcastCounters(); render(); setupDropZones();
        ctx.onDone();
      },
      card
    );
  },
});

// ─── GD04-001 · Gundam (White Base Team Lv.6) ────────────────────────────────
// [During Link][Attack] If you are attacking an enemy Unit, you may return a
// blue Pilot paired with this Unit to its owner's hand.
// ALTP1 identical.
function gd04001AttackEffect(card, ctx) {
  if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
  if (!isLinkUnit(card)) { ctx.onDone?.(); return; }
  if (ctx.isBase || ctx.isShield || ctx.isDirect) { ctx.onDone?.(); return; }
  if (!card.pilot || (card.pilot.color || '').toLowerCase() !== 'blue') { ctx.onDone?.(); return; }
  showCardPickerModal(
    card.name,
    'You may return the blue Pilot paired with this Unit to your hand.',
    [card.pilot],
    (chosen) => {
      if (chosen) {
        const pilot = card.pilot;
        card.pilot = null;
        card.pilotKeywords = [];
        state.hand.push(pilot);
        updateTempState(pilot.cardNo, pilot.copyVer, 'hand');
        const slot = state.playerField.findIndex(c => c && c.uid === card.uid);
        if (slot !== -1) {
          const { selected, ...cardCopy } = card;
          NET.send({ type: 'PLAY_CARD', slot, card: cardCopy });
        }
        addLog(`${card.name} — ${pilot.name} returned to hand.`);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone?.();
    },
    card
  );
}
registerCardEffect('GD04-001',      { attack: gd04001AttackEffect });
registerCardEffect('GD04-001-ALTP1', { attack: gd04001AttackEffect });

// ─── GD04-002 · Penelope (Flight Form) ───────────────────────────────────────
// During your turn, all your (Earth Federation) Units get AP+1.
// [Deploy] During this turn, when one of your (Earth Federation) Units destroys
// an enemy Unit with battle damage, choose 1 enemy Unit with 5 or less HP. Rest it.
// ALTP1 identical.
function gd04002DeployWatcher(card) {
  const watcherName = card.name;
  turnDestroyWatchers.push((destroyer, _destroyed, destroyerSide, targetType) => {
    // Card text says "an enemy Unit" specifically — unlike GD05-002 Strike
    // Freedom Gundam's "an enemy card" (see turnDestroyWatchers in game.js),
    // a shield or base kill must NOT trigger this.
    if (targetType !== 'unit') return;
    if (destroyerSide !== 'player') return;
    if (!(destroyer.trait || '').includes('Earth Federation')) return;
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 5);
    if (candidates.length === 0) return;
    showCardPickerModal(
      watcherName,
      `${destroyer.name} destroyed an enemy! Choose 1 active enemy Unit with 5 or less HP. Rest it.`,
      candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${watcherName} — ${target.name} rested (EF Unit destroyed enemy).`);
          render(); setupDropZones();
        }
      },
      null, { mandatory: true }
    );
  });
}
const gd04002Handlers = {
  // "During your turn" relative to ownerSide, not state.myTurn directly —
  // see ST01-001 Gundam for the full rationale (the old ownerSide==='player'
  // short-circuit made this always return 0 from the non-controlling
  // client's mirrored perspective, causing an actual HP desync in networked
  // play).
  globalUnitApBonus: (targetCard, _src, ownerSide) => {
    const isControllersTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    return isControllersTurn && (targetCard.trait || '').includes('Earth Federation') ? 1 : 0;
  },
  deploy: (card) => gd04002DeployWatcher(card),
};
registerCardEffect('GD04-002',      gd04002Handlers);
registerCardEffect('GD04-002-ALTP1', gd04002Handlers);

// ─── GD04-003 · Victory Gundam (Lv.4) ────────────────────────────────────────
// [Attack] If you have 3 or more (League Militaire) Units in play, draw 1.
// ALTP1 identical.
function gd04003AttackEffect(card, ctx) {
  if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
  const lmCount = state.playerField.filter(c => c && (c.trait || '').includes('League Militaire')).length;
  if (lmCount < 3) {
    addLog(`${card.name} — need 3+ (League Militaire) Units (have ${lmCount}).`);
    ctx.onDone?.(); return;
  }
  const drawn = drawCardsFromTempDeck(1, true);
  if (drawn.length > 0) {
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name} (3+ LM Units).`);
  }
  broadcastCounters(); render();
  ctx.onDone?.();
}
registerCardEffect('GD04-003',      { attack: gd04003AttackEffect });
registerCardEffect('GD04-003-ALTP1', { attack: gd04003AttackEffect });

// ─── GD04-004 · Psycho Gundam Mk-II ─────────────────────────────────────────
// <Repair 2>
// [Once per Turn] When you pair a (Cyber-Newtype) Pilot with one of your blue
// Units, draw 1.
function gd04004TryDraw(card, pilotTrait) {
  if (card.passiveUsedThisTurn) return;
  if (!(pilotTrait || '').includes('Cyber-Newtype')) return;
  card.passiveUsedThisTurn = true;
  const drawn = drawCardsFromTempDeck(1, true);
  if (drawn.length > 0) {
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name} (Cyber-Newtype paired with blue Unit).`);
  }
  broadcastCounters(); render();
}
registerCardEffect('GD04-004', {
  pair: (card) => gd04004TryDraw(card, card.pilot?.trait),
  onAnyUnitPair: (card, pairedUnit, pairedColor) => {
    if (pairedColor !== 'blue') return;
    gd04004TryDraw(card, pairedUnit.pilot?.trait);
  },
});

// ─── GD04-006 · V-Dash Gundam ────────────────────────────────────────────────
// <Breach 3>
// [Activate･Main][Once per Turn] Rest 1 of your other (League Militaire) Units:
// Choose 1 enemy Unit with 4 or less HP. Rest it.
// ALTP1 identical.
function gd04006ActivateEffect(card, ctx) {
  card.activateMainUsedThisTurn = true;
  const lmCandidates = state.playerField.filter(c =>
    c && c.uid !== card.uid && !c.rested && (c.trait || '').includes('League Militaire')
  );
  if (lmCandidates.length === 0) {
    addLog(`${card.name} — no active (League Militaire) Units to rest as cost.`);
    card.activateMainUsedThisTurn = false;
    ctx.onDone(); return;
  }
  showCardPickerModal(
    card.name, 'Rest 1 of your other active (League Militaire) Units as cost.', lmCandidates,
    (costUnit) => {
      if (!costUnit) { card.activateMainUsedThisTurn = false; ctx.onDone(); return; }
      setRested(costUnit, true);
      addLog(`${card.name} — rested ${costUnit.name} as cost.`);
      render(); setupDropZones();
      const enemyCandidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 4);
      if (enemyCandidates.length === 0) {
        addLog(`${card.name} — no active enemy Units with 4 or less HP.`);
        ctx.onDone(); return;
      }
      showCardPickerModal(
        card.name, 'Choose 1 active enemy Unit with 4 or less HP. Rest it.', enemyCandidates,
        (target) => {
          if (target) {
            setRested(target, true);
            addLog(`${card.name} — ${target.name} rested.`);
            render(); setupDropZones();
          }
          ctx.onDone();
        },
        card, { mandatory: true }
      );
    },
    card, { mandatory: true }
  );
}
const gd04006Handlers = {
  canActivate: (card) =>
    !card.activateMainUsedThisTurn &&
    state.playerField.some(c => c && c.uid !== card.uid && !c.rested && (c.trait || '').includes('League Militaire')) &&
    state.oppField.some(c => c && !c.rested && effectiveHp(c) <= 4),
  activate: gd04006ActivateEffect,
};
registerCardEffect('GD04-006',      gd04006Handlers);
registerCardEffect('GD04-006-ALTP1', gd04006Handlers);

// ─── GD04-007 · Victory Gundam Hexa ─────────────────────────────────────────
// [During Pair][Attack] Deploy 1 [Parts]((League Militaire)·AP1·HP1) Unit token.
registerCardEffect('GD04-007', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!card.pilot) { ctx.onDone?.(); return; }
    deployTokenToField('T-021');
    addLog(`${card.name} — deployed a [Parts] (League Militaire) token.`);
    broadcastCounters(); render(); setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD04-008 · Gundam (White Base Team Lv.5) ────────────────────────────────
// [During Link] This Unit gains <High-Maneuver>.
registerCardEffect('GD04-008', {
  passiveKeywords: (card) => isLinkUnit(card) ? ['High-Maneuver'] : [],
});

// ─── GD04-009 · Guncannon (108) & Guncannon (109) ────────────────────────────
// [When Linked] Choose 1 of your other (White Base Team) Units that is Lv.4 or
// higher. Set it as active.
// ALTP1 identical.
function gd04009PairEffect(card) {
  if (!isLinkUnit(card)) return;
  const candidates = state.playerField.filter(c =>
    c && c.uid !== card.uid && (c.trait || '').includes('White Base Team') && (parseInt(c.level) || 0) >= 4
  );
  if (candidates.length === 0) {
    addLog(`${card.name} — no other (White Base Team) Units Lv.4+.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 of your other (White Base Team) Units Lv.4 or higher. Set it as active.', candidates,
    (target) => {
      if (target) {
        setRested(target, false);
        addLog(`${card.name} — ${target.name} set active.`);
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('GD04-009',      { pair: gd04009PairEffect });
registerCardEffect('GD04-009-ALTP1', { pair: gd04009PairEffect });

// ─── GD04-011 · Victory Gundam (Lv.3) ────────────────────────────────────────
// [Destroyed] If another friendly (League Militaire) Unit is in play, deploy 1
// [Parts]((League Militaire)·AP1·HP1) Unit token.
registerCardEffect('GD04-011', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (ownerSide !== 'player') return;
    if (!state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('League Militaire'))) return;
    deployTokenToField('T-021');
    addLog(`${card.name} — [Destroyed] deployed a [Parts] (League Militaire) token.`);
    broadcastCounters(); render(); setupDropZones();
  },
});

// ─── GD04-013 · Core Fighter ─────────────────────────────────────────────────
// While this Unit is rested, all your (League Militaire) Unit tokens gain
// <Blocker>.
registerCardEffect('GD04-013', {
  globalUnitKeyword: (targetCard, sourceCard) => {
    if (!sourceCard.rested) return [];
    if (!isUnitToken(targetCard)) return [];
    if (!(targetCard.trait || '').includes('League Militaire')) return [];
    return ['Blocker'];
  },
});

// ─── GD04-015 · Gun EZ ───────────────────────────────────────────────────────
// [Deploy] Choose 1 of your active (League Militaire) Units and 1 enemy Unit
// that is Lv.3 or lower. Rest them.
registerCardEffect('GD04-015', {
  deploy: (card) => {
    const friendlyCandidates = state.playerField.filter(c =>
      c && !c.rested && (c.trait || '').includes('League Militaire')
    );
    const enemyCandidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 3);
    if (friendlyCandidates.length === 0 || enemyCandidates.length === 0) {
      addLog(`${card.name} — no valid targets for [Deploy] effect.`);
      return;
    }
    showCardPickerModal(
      card.name, 'Choose 1 of your active (League Militaire) Units to rest.', friendlyCandidates,
      (friendly) => {
        if (!friendly) return;
        setRested(friendly, true);
        addLog(`${card.name} — rested ${friendly.name}.`);
        render(); setupDropZones();
        showCardPickerModal(
          card.name, 'Choose 1 active enemy Unit that is Lv.3 or lower. Rest it.', enemyCandidates,
          (enemy) => {
            if (enemy) {
              setRested(enemy, true);
              addLog(`${card.name} — ${enemy.name} rested.`);
              render(); setupDropZones();
            }
          },
          card, { mandatory: true }
        );
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD04-016 · Zoloat (League Militaire) ────────────────────────────────────
// <Blocker>
// This Unit can't choose the enemy player as its attack target.
registerCardEffect('GD04-016', { cantAttackNonUnit: () => true });

// ─── ST01-001 · Gundam (White Base Team) ─────────────────────────────────────
// <Repair 2>
// [During Pair] During your turn, all your Units get AP+1.
// ALTP1–ALTP7 identical.
// "During your turn" must be read relative to ownerSide (this hook's 3rd
// param), not state.myTurn directly — state.myTurn only ever means "my own
// turn from THIS client's local perspective". Networked, a card gets scanned
// from BOTH clients' perspectives: on the controller's own client ownerSide
// is 'player' and state.myTurn correctly means their own turn, but on the
// OTHER client's mirrored view of that same card, ownerSide is 'opp' and it
// is the controller's turn exactly when state.myTurn is FALSE there. The old
// `ownerSide === 'player'` condition made this hook return 0 whenever
// ownerSide was 'opp' — always, regardless of whose turn it actually was —
// so a unit boosted by an [Attack] effect (or any other AP mod) while paired
// Gundam's own aura should also apply came out 1 short specifically on the
// non-controlling client, causing an actual HP desync (that client dealt/
// took 1 less damage than the controller's own client computed). Same
// isControllersTurn pattern as GD05-065 Landman Rodi.
const st01001Handlers = {
  globalUnitApBonus: (_targetCard, sourceCard, ownerSide) => {
    if (!sourceCard.pilot) return 0;
    const isControllersTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    return isControllersTurn ? 1 : 0;
  },
};
registerCardEffect('ST01-001',      st01001Handlers);
registerCardEffect('ST01-001-ALTP1', st01001Handlers);
registerCardEffect('ST01-001-ALTP2', st01001Handlers);
registerCardEffect('ST01-001-ALTP3', st01001Handlers);
registerCardEffect('ST01-001-ALTP4', st01001Handlers);
registerCardEffect('ST01-001-ALTP5', st01001Handlers);
registerCardEffect('ST01-001-ALTP6', st01001Handlers);
registerCardEffect('ST01-001-ALTP7', st01001Handlers);

// ─── ST01-002 · Gundam (MA Form) ─────────────────────────────────────────────
// [When Paired·(White Base Team) Pilot] Draw 1.
// ALTP1/ALTP2 identical.
function st01002PairEffect(card, ctx) {
  if (!(ctx?.unit?.pilot?.trait || '').includes('White Base Team')) return;
  const drawn = drawCardsFromTempDeck(1, true);
  if (drawn.length > 0) {
    state.hand.push(drawn[0]);
    addLog(`${card.name} — [When Paired·WBT Pilot] drew ${drawn[0].name}.`);
  }
  broadcastCounters(); render();
}
registerCardEffect('ST01-002',      { pair: st01002PairEffect });
registerCardEffect('ST01-002-ALTP1', { pair: st01002PairEffect });
registerCardEffect('ST01-002-ALTP2', { pair: st01002PairEffect });

// ─── ST01-004 · Guntank (White Base Team) ────────────────────────────────────
// [Deploy] Choose 1 enemy Unit with 2 or less HP. Rest it.
// ALTP1 identical.
function st01004DeployEffect(card) {
  const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 2 or less HP.`);
    return;
  }
  showCardPickerModal(
    card.name, 'Choose 1 active enemy Unit with 2 or less HP. Rest it.', candidates,
    (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — ${target.name} rested.`);
        render(); setupDropZones();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('ST01-004',      { deploy: st01004DeployEffect });
registerCardEffect('ST01-004-ALTP1', { deploy: st01004DeployEffect });

// ─── ST02-006 · Tallgeese (OZ Lv.5) ─────────────────────────────────────────
// [Activate･Main][Once per Turn] ④: Set this Unit as active.
// ALTP1 identical.
function st02006ActivateEffect(card, ctx) {
  card.activateMainUsedThisTurn = true;
  if ((state.res + state.exRes) < 4) {
    addLog(`${card.name} — need ④ RES (have ${state.res + state.exRes}). No effect.`);
    card.activateMainUsedThisTurn = false;
    ctx.onDone(); return;
  }
  spendResources(4);
  setRested(card, false);
  addLog(`${card.name} — spent ④, set active.`);
  broadcastCounters(); render(); setupDropZones();
  ctx.onDone();
}
const st02006Handlers = {
  canActivate: (card) =>
    !card.activateMainUsedThisTurn && card.rested && (state.res + state.exRes) >= 4,
  activate: st02006ActivateEffect,
};
registerCardEffect('ST02-006',      st02006Handlers);
registerCardEffect('ST02-006-ALTP1', st02006Handlers);

// ─── ST08-006 · Penelope (Lv.7) ──────────────────────────────────────────────
// [During Pair][Attack][Once per Turn] If this Unit is attacking the enemy player,
// reveal 1 (Earth Federation) Unit card from your hand. Return it to the bottom
// of your deck. If you do, draw 2.
// "Attacking the enemy player" covers Base/Shield/Direct attacks — anything
// that isn't targeting an enemy Unit — see isAttackingEnemyPlayer() in white.js.
// ALTP1 identical.
function st08006AttackEffect(card, ctx) {
  if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
  if (!card.pilot) { ctx.onDone?.(); return; }
  if (!isAttackingEnemyPlayer(ctx)) { ctx.onDone?.(); return; }
  if (card.passiveUsedThisTurn) { ctx.onDone?.(); return; }
  const candidates = state.hand.filter(c => c.type === 'unit' && (c.trait || '').includes('Earth Federation'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no (Earth Federation) Unit cards in hand.`);
    ctx.onDone?.(); return;
  }
  card.passiveUsedThisTurn = true;
  showCardPickerModal(
    card.name,
    'Reveal 1 (Earth Federation) Unit card from your hand. Return it to the bottom of your deck and draw 2.',
    candidates,
    (chosen) => {
      if (!chosen) { card.passiveUsedThisTurn = false; ctx.onDone?.(); return; }
      NET.send({ type: 'CARD_REVEAL', card: chosen, pilotName: card.name });
      state.hand = state.hand.filter(c => c.uid !== chosen.uid);
      const entry = state.tempDeck && state.tempDeck.find(
        e => e.cardNo === chosen.cardNo && e.copyVer === chosen.copyVer
      );
      if (entry) sendDeckEntryToBottom(entry);
      state.deckCount = (state.tempDeck || []).filter(isInDeck).length;
      addLog(`${card.name} — revealed and returned ${chosen.name} to deck bottom.`);
      const drawn = drawCardsFromTempDeck(2, true);
      drawn.forEach(d => state.hand.push(d));
      if (drawn.length > 0) addLog(`${card.name} — drew ${drawn.map(d => d.name).join(', ')}.`);
      broadcastCounters(); render(); setupDropZones();
      ctx.onDone?.();
    },
    card, { mandatory: true }
  );
}
registerCardEffect('ST08-006',      { attack: st08006AttackEffect });
registerCardEffect('ST08-006-ALTP1', { attack: st08006AttackEffect });

// ─── ST08-008 · Gustav Karl Type-00 ─────────────────────────────────────────
// While 3 or more enemy Units are in play, this Unit gains <Blocker>.
// ALTP1 identical.
const st08008Handlers = {
  passiveKeywords: () =>
    state.oppField.filter(c => c).length >= 3 ? ['Blocker'] : [],
};
registerCardEffect('ST08-008',      st08008Handlers);
registerCardEffect('ST08-008-ALTP1', st08008Handlers);

// ─── ST08-009 · Jegan Ground Type-A (Man Hunter) ─────────────────────────────
// [Deploy] Choose 1 rested enemy Unit that is Lv.2 or lower. It won't be set as
// active during the start phase of your opponent's next turn.
// ALTP1 identical.
function st08009DeployEffect(card) {
  const candidates = state.oppField.filter(c => c && c.rested && (parseInt(c.level) || 0) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no rested enemy Units Lv.2 or lower.`);
    return;
  }
  showCardPickerModal(
    card.name,
    'Choose 1 rested enemy Unit (Lv.2 or lower). It won\'t unrest at start of opponent\'s next turn.',
    candidates,
    (target) => {
      if (target) {
        target.keepRestedNextTurn = true;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'keepRestedNextTurn' });
        addLog(`${card.name} — ${target.name} won't unrest next turn.`);
        render();
      }
    },
    card, { mandatory: true }
  );
}
registerCardEffect('ST08-009',      { deploy: st08009DeployEffect });
registerCardEffect('ST08-009-ALTP1', { deploy: st08009DeployEffect });

// ════════════════════════════════════════════════════════════════════════════
// GD05 · FREEDOM ASCENSION — BLUE
// ════════════════════════════════════════════════════════════════════════════

// ─── GD05-001 · V2 Gundam ─────────────────────────────────────────────────
// <Repair 2> native (handled generically).
// [Activate･Main][Once per Turn] Rest 2 of your Units: Set this Unit as active.
registerCardEffect('GD05-001', {
  activate: (card, ctx) => {
    setRested(card, false);
    addLog(`${card.name} — [Activate･Main] set itself as active.`);
    render();
    setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD05-002 · Strike Freedom Gundam ─────────────────────────────────────
// [Deploy] Choose 1 to 2 of your Units. During this turn, when they destroy
// an enemy card with battle damage, draw 1.
// [During Pair][Attack] You may discard 2. If you do, choose 1 enemy Unit
// with the lowest Lv. Return it to the bottom of its owner's deck.
// "an enemy card" — deliberately broader than the "enemy Unit" wording most
// other turnDestroyWatchers users have (GD04-002, GD03-120, GD04-035,
// ST12-016 — see their own guards in game.js's turnDestroyWatchers comment).
// This watcher intentionally does NOT check targetType, so it fires for a
// Unit, a Shield, or a Base destroyed by battle damage alike.
registerCardEffect('GD05-002', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — no friendly Units. No effect.`); return; }
    showCardPickerModalMulti(
      card.name,
      'Choose 1 to 2 of your Units. They draw 1 when they destroy an enemy card with battle damage this turn.',
      candidates,
      { min: 1, max: Math.min(2, candidates.length) },
      (chosen) => {
        if (chosen.length === 0) return;
        const uids = new Set(chosen.map(c => c.uid));
        turnDestroyWatchers.push((destroyer) => {
          if (!uids.has(destroyer.uid)) return;
          const drawn = drawCardsFromTempDeck(1, true);
          if (drawn.length > 0) {
            state.hand.push(drawn[0]);
            addLog(`${card.name} — ${destroyer.name} destroyed an enemy card with battle damage: drew 1.`);
            broadcastCounters();
            render();
          }
        });
        addLog(`${card.name} — ${chosen.map(c => c.name).join(', ')} will draw 1 when they destroy an enemy card with battle damage this turn.`);
      },
      card
    );
  },
  attack: (card, ctx) => {
    if (!card.pilot) { ctx.onDone(); return; }
    if (state.hand.length < 2) { ctx.onDone(); return; }
    showCardPickerModalMulti(
      card.name,
      "You may discard 2. If you do, choose 1 enemy Unit with the lowest Lv. Return it to the bottom of its owner's deck.",
      [...state.hand],
      { min: 0, max: 2 },
      (chosen) => {
        if (chosen.length !== 2) { ctx.onDone(); return; }
        chosen.forEach(c => {
          state.hand = state.hand.filter(h => h.uid !== c.uid);
          updateTempState(c.cardNo, c.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(c));
          broadcastCardToast(`${c.name} was discarded due to ${card.name} effect.`);
        });
        broadcastLog(`${card.name} — discarded ${chosen.map(c => c.name).join(', ')}.`);
        const enemies = state.oppField.filter(c => c);
        if (enemies.length === 0) {
          broadcastCounters();
          render();
          ctx.onDone();
          return;
        }
        const lowestLv = Math.min(...enemies.map(c => parseInt(c.level) || 0));
        const targets = enemies.filter(c => (parseInt(c.level) || 0) === lowestLv);
        showCardPickerModal(card.name, "Choose 1 enemy Unit with the lowest Lv. Return it to the bottom of its owner's deck.", targets, (target) => {
          if (target) {
            bounceEnemyUnitToDeckBottom(target);
            addLog(`${card.name} — returned ${target.name} to the bottom of its owner's deck.`);
            render();
            setupDropZones();
          }
          broadcastCounters();
          ctx.onDone();
        }, card, { mandatory: true });
      },
      card
    );
  },
});

// ─── GD05-003 · Waldfeld's Murasame ───────────────────────────────────────
// [Destroyed] If you have an (Orb) Pilot in play, draw 1.
registerCardEffect('GD05-003', {
  destroyed: (card) => {
    const hasOrbPilot = state.playerField.some(c => c && c.pilot && (c.pilot.trait || '').includes('(Orb)'));
    if (!hasOrbPilot) {
      addLog(`${card.name} — [Destroyed] no (Orb) Pilot in play. No effect.`);
      return;
    }
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Destroyed] (Orb) Pilot in play: drew 1.`);
      broadcastCounters();
      render();
    }
  },
});

// ─── GD05-004 · Akatsuki (Oowashi) ────────────────────────────────────────
// While you have no Units that are Lv.6 or higher in play, this card in
// your hand gets Lv. -1 and cost -1 for each of your (Orb) Units in play.
// [When Linked] Choose 1 enemy Unit that is Lv.4 or lower. Return it to its
// owner's hand.
function gd05004Reduction() {
  if (state.playerField.some(c => c && (parseInt(c.level) || 0) >= 6)) return 0;
  return state.playerField.filter(c => c && (c.trait || '').includes('(Orb)')).length;
}
registerCardEffect('GD05-004', {
  handLevelReduction: gd05004Reduction,
  handCostReduction: gd05004Reduction,
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Linked] no enemy Units Lv.4 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit that is Lv.4 or lower. Return it to its owner's hand.", candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — [When Linked] returned ${target.name} to its owner's hand.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD05-006 · Hashmal ────────────────────────────────────────────────────
// [Once per Turn] During your turn, when this Unit destroys an enemy card
// with battle damage, deploy 1 [Pluma]((Calamity War)･AP2･HP1) Unit token.
// This Unit gains the same number of <Repair 1> as the number of (Calamity
// War) Unit tokens you have in play.
registerCardEffect('GD05-006', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const token = deployTokenToField('T-027');
    if (token) {
      addLog(`${card.name} — [Once per Turn] destroyed an enemy card with battle damage: deployed 1 ${token.name} Unit token.`);
    } else {
      addLog(`${card.name} — [Once per Turn] field is full, could not deploy Pluma token.`);
    }
  },
  conditionalRepair: (card) =>
    state.playerField.filter(c => c && isUnitToken(c) && (c.trait || '').includes('(Calamity War)')).length,
});

// ─── GD05-007 · Asshimar ───────────────────────────────────────────────────
// [During Link] This Unit gets AP+2 and <Repair 1>.
registerCardEffect('GD05-007', {
  passiveUnitApBonus: (card) => isLinkUnit(card) ? 2 : 0,
  conditionalRepair: (card) => isLinkUnit(card) ? 1 : 0,
});

// ─── GD05-008 · Dijeh ──────────────────────────────────────────────────────
// While you have a non-blue (Newtype) Pilot in play, this card in your hand
// gets cost -2.
registerCardEffect('GD05-008', {
  handCostReduction: () => {
    const has = state.playerField.some(c =>
      c && c.pilot && (c.pilot.trait || '').includes('(Newtype)') && (c.pilot.color || '').toLowerCase() !== 'blue'
    );
    return has ? 2 : 0;
  },
});

// ─── GD05-011 · Calamity Gundam & Raider Gundam ───────────────────────────
// [Deploy] You may choose 1 of your other active (Earth Alliance) Units.
// Rest it. If you do, choose 1 rested enemy Unit. Deal 2 damage to it.
registerCardEffect('GD05-011', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid && !c.rested && (c.trait || '').includes('(Earth Alliance)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no other active (Earth Alliance) Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'You may choose 1 of your other active (Earth Alliance) Units. Rest it.', candidates, (target) => {
      if (!target) return;
      setRested(target, true);
      addLog(`${card.name} — rested ${target.name}.`);
      const enemies = state.oppField.filter(c => c && c.rested);
      if (enemies.length === 0) {
        addLog(`${card.name} — no rested enemy Units. No effect.`);
        render();
        setupDropZones();
        return;
      }
      showCardPickerModal(card.name, 'Choose 1 rested enemy Unit. Deal 2 damage to it.', enemies, (enemyTarget) => {
        if (enemyTarget) {
          dealDamageToUnit(enemyTarget, 2, { fromEnemy: true });
          addLog(`${card.name} — dealt 2 damage to ${enemyTarget.name}.`);
          render();
          setupDropZones();
        }
      }, card, { mandatory: true });
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// ─── GD05-012 · Forbidden Gundam ──────────────────────────────────────────
// [When Linked] Choose 1 rested enemy Unit that is Lv.3 or lower. Return it
// to its owner's hand.
registerCardEffect('GD05-012', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const candidates = state.oppField.filter(c => c && c.rested && (parseInt(c.level) || 0) <= 3);
    pickAndBounceOne(card, candidates, "Choose 1 rested enemy Unit that is Lv.3 or lower. Return it to its owner's hand.", () => {});
  },
});

// ─── GD05-015 · M1 Astray Shrike ──────────────────────────────────────────
// [Deploy] Choose 1 rested enemy Unit. Deal 1 damage to it.
registerCardEffect('GD05-015', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD05-016 · Murasame ───────────────────────────────────────────────────
// When one of your (Orb) Units is deployed, this Unit gains <High-Maneuver>
// during this turn.
registerCardEffect('GD05-016', {
  onAnyPlayerUnitDeployed: (card, deployedCard) => {
    if (!(deployedCard.trait || '').includes('(Orb)')) return;
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('High-Maneuver')) card.tempKeywords.push('High-Maneuver');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'High-Maneuver' });
    addLog(`${card.name} — ${deployedCard.name} deployed: gains <High-Maneuver> this turn.`);
    render();
  },
});

// ─── GD05-081 · Kira Yamato — Pilot ───────────────────────────────────────
// [Burst] Add this card to your hand — handled generically.
// [When Linked] If this is an (Orb)/(Triple Ship Alliance) Unit, draw 1.
registerCardEffect('GD05-081', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) return;
    const trait = ctx.unit.trait || '';
    if (!trait.includes('(Orb)') && !trait.includes('(Triple Ship Alliance)')) {
      addLog(`${pilot.name} — [When Linked] not an (Orb)/(Triple Ship Alliance) Unit. No effect.`);
      return;
    }
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${pilot.name} — [When Linked] drew 1.`);
      broadcastCounters();
      render();
    }
  },
});

// ─── GD05-082 · Andrew Waldfeld — Pilot ───────────────────────────────────
// [Burst] Add this card to your hand — handled generically.
// [During Link] This Unit gains <Repair 2>.
// pilotConditionalRepairValue(pilot, unit) is the pilot-side counterpart of
// conditionalRepair(unit) — same precedence in getRepairValue(), for a
// Pilot granting Repair to whatever Unit it's linked with (matches GD01-087
// Sayla Mass's existing convention).
registerCardEffect('GD05-082', {
  pilotConditionalRepairValue: (pilot, unit) => isLinkUnit(unit) ? 2 : 0,
});

// ─── GD05-083 · Cagalli Yula Athha — Pilot ────────────────────────────────
// [Burst] Add this card to your hand — handled generically.
// [When Paired] Choose 1 enemy Unit with 1 HP. Return it to its owner's hand.
registerCardEffect('GD05-083', {
  pair: (pilot) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) === 1);
    pickAndBounceOne(pilot, candidates, "Choose 1 enemy Unit with 1 HP. Return it to its owner's hand.", () => {});
  },
});

// ─── GD05-084 · Odelo Henrik — Pilot ──────────────────────────────────────
// [Burst] Add this card to your hand — handled generically.
// When one of your (League Militaire) Unit tokens receives enemy effect
// damage, reduce it by 1. Field-wide (applies regardless of which Unit he's
// actually paired with) — see passiveFieldTokenDamageReduction in
// registry.js / dealDamageToUnit().
registerCardEffect('GD05-084', {
  passiveFieldTokenDamageReduction: (pilot, targetToken) => {
    if (!isUnitToken(targetToken)) return 0;
    if (!(targetToken.trait || '').includes('(League Militaire)')) return 0;
    return 1;
  },
});

// ─── GD05-102 · Wings of Light — Command ──────────────────────────────────
// [Action] When playing this card, choose 1 of the following effects and
// activate it:
// ■ Choose 1 enemy Unit with 5 or less HP. Return it to its owner's hand.
// ■ Choose 1 Unit. It recovers 3 HP.
// No existing "choose 1 of N abstract effects" picker exists in this
// codebase (only card/unit-target pickers do) — showChooseOneEffectModal is
// a small bespoke menu modal (same .mk-overlay/.mk-modal boilerplate as
// showActivationChoiceModal) with each ■ bullet point turned into its own
// clickable option; picking one runs that option's own logic (which may
// itself open a further showCardPickerModal for the actual target).
function showChooseOneEffectModal(card, options, onDone) {
  const overlay = document.createElement('div');
  overlay.className = 'mk-overlay';
  const modal = document.createElement('div');
  modal.className = 'mk-modal';

  const title = document.createElement('div');
  title.className = 'mk-title';
  title.textContent = card.name;
  modal.appendChild(title);

  const body = document.createElement('div');
  body.className = 'mk-body';
  body.style.marginBottom = '14px';
  body.textContent = 'Choose 1 of the following effects to activate:';
  modal.appendChild(body);

  const btnRow = document.createElement('div');
  btnRow.className = 'base-activate-btns';
  btnRow.style.flexDirection = 'column';
  btnRow.style.gap = '8px';
  btnRow.style.alignItems = 'stretch';

  options.forEach(opt => {
    const btn = document.createElement('button');
    btn.className = 'btn-primary';
    btn.textContent = opt.label;
    btn.style.whiteSpace = 'normal';
    btn.style.textAlign = 'left';
    btn.addEventListener('click', () => {
      overlay.remove();
      opt.run(onDone);
    });
    btnRow.appendChild(btn);
  });
  modal.appendChild(btnRow);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => { btnRow.querySelector('button')?.click(); });
}

function gd05102Effect(card, onDone) {
  showChooseOneEffectModal(card, [
    {
      label: "Choose 1 enemy Unit with 5 or less HP. Return it to its owner's hand.",
      run: (done) => {
        const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 5);
        pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 5 or less HP. Return it to its owner's hand.", done);
      },
    },
    {
      label: 'Choose 1 Unit. It recovers 3 HP.',
      run: (done) => {
        const candidates = [...state.playerField, ...state.oppField].filter(c => c);
        if (candidates.length === 0) {
          addLog(`${card.name} — no Units in play. No effect.`);
          done();
          return;
        }
        showCardPickerModal(card.name, 'Choose 1 Unit. It recovers 3 HP.', candidates, (target) => {
          if (target) {
            applyHpMod([target], 3);
            addLog(`${card.name} — ${target.name} recovers 3 HP.`);
            render();
            setupDropZones();
          }
          done();
        }, card, { mandatory: true });
      },
    },
  ], onDone);
}
registerCardEffect('GD05-102', {
  action: (card, ctx) => gd05102Effect(card, ctx.onDone),
});

// ─── GD05-103 · Not with Scattershot! — Command Pilot ─────────────────────
// [Main]/[Action] Choose 1 friendly Unit. It recovers 1 HP and gets AP+2
// during this turn.
// [Pilot] Buran Blutarch
function gd05103Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c);
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly Units. No effect.`);
    onDone();
    return;
  }
  showCardPickerModal(card.name, 'Choose 1 friendly Unit. It recovers 1 HP and gets AP+2 during this turn.', candidates, (target) => {
    if (target) {
      applyHpMod([target], 1);
      applyApMod([target], 2, 'tempApMod');
      addLog(`${card.name} — ${target.name} recovers 1 HP and gets AP+2 during this turn.`);
      render();
      setupDropZones();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD05-103', {
  main:   (card, ctx) => gd05103Effect(card, ctx.onDone),
  action: (card, ctx) => gd05103Effect(card, ctx.onDone),
});

// ─── GD05-104 · At the Risk of One's Life — Command Pilot ─────────────────
// [Action] Choose 1 friendly (Shrike Team) Unit. It gains the following
// effect during this turn:
// ■ [During Link][Destroyed] Choose 1 friendly (League Militaire) Unit. Set
//   it as active.
// [Pilot] Helen Jackson
// Uses registerTempGrantedAbility() (see game.js's triggerCardEffect) so the
// granted [Destroyed] ability fires alongside the target unit's own
// registered 'destroyed' handler (if any) for the rest of the turn, and
// only when isLinkUnit(target) is true at the moment it's actually
// destroyed — a unit with no link requirement at all can never satisfy it.
registerCardEffect('GD05-104', {
  action: (pilot, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Shrike Team)'));
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no friendly (Shrike Team) Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      pilot.name,
      'Choose 1 friendly (Shrike Team) Unit. It gains an effect until the end of this turn.',
      candidates,
      (target) => {
        if (target) {
          registerTempGrantedAbility(target, {
            trigger: 'destroyed',
            requiresLink: true,
            handler: (card) => {
              const leagueCandidates = state.playerField.filter(c => c && (c.trait || '').includes('(League Militaire)'));
              if (leagueCandidates.length === 0) {
                addLog(`${card.name} — [During Link][Destroyed] no friendly (League Militaire) Units to set active.`);
                return;
              }
              showCardPickerModal(
                card.name,
                'Choose 1 friendly (League Militaire) Unit. Set it as active.',
                leagueCandidates,
                (chosen) => {
                  if (chosen) {
                    setRested(chosen, false);
                    addLog(`${card.name} — [During Link][Destroyed]: set ${chosen.name} as active.`);
                    render();
                    setupDropZones();
                  }
                },
                card,
                { mandatory: false }
              );
            },
          });
          addLog(`${pilot.name} — ${target.name} gains [During Link][Destroyed] effect until the end of the turn.`);
          render();
        }
        ctx.onDone?.();
      },
      pilot,
      { mandatory: false, skipLabel: 'Skip' }
    );
  },
});

// ─── GD05-105 · Exclusively Defense-Oriented Policy — Command ─────────────
// [Burst] Activate this card's [Main].
// [Main]/[Action] Choose 1 enemy Unit that is Lv.3 or lower. Return it to
// its owner's hand.
function gd05105Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
  pickAndBounceOne(card, candidates, "Choose 1 enemy Unit that is Lv.3 or lower. Return it to its owner's hand.", onDone);
}
registerCardEffect('GD05-105', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 3),
  burst:  (card, ctx) => gd05105Effect(card, ctx?.onDone ?? (() => {})),
  main:   (card, ctx) => gd05105Effect(card, ctx.onDone),
  action: (card, ctx) => gd05105Effect(card, ctx.onDone),
});

// ─── GD05-123 · Archangel — Base ──────────────────────────────────────────
// [Burst]/[Deploy] handled generically (shield to hand).
// During your opponent's turn, friendly (Orb) Units can't receive 2 or less
// enemy effect damage. baseEffectDamageBlock receives the live incoming
// amount (unlike effectDamageReduction) so it can fully block up to a
// threshold instead of just subtracting a flat number — see dealDamageToUnit.
registerCardEffect('GD05-123', {
  baseEffectDamageBlock: (base, unit, amount) => {
    const ownerSide = (state.playerBase && state.playerBase.uid === base.uid) ? 'player' : 'opp';
    const isOwnersOpponentTurn = ownerSide === 'player' ? !state.myTurn : state.myTurn;
    if (!isOwnersOpponentTurn) return 0;
    if (!(unit.trait || '').includes('(Orb)')) return 0;
    return amount <= 2 ? amount : 0;
  },
});

// ─── GD05-124 · White Ark — Base ──────────────────────────────────────────
// [Burst]/[Deploy] handled generically (shield to hand).
// "During your turn, when you would rest a Unit with a friendly (League
// Militaire) Unit's effect, you may rest this Base instead." — NOT YET
// wired into any interception point (see leagueMilitaireRestSubstitute doc
// in registry.js): no current card effect in this codebase rests a friendly
// unit as part of its own resolution, so this flag is presently a
// documented no-op until such an effect exists and calls the (not-yet-
// written) restFriendlyUnitOrSubstitute() helper instead of setting
// `target.rested = true` directly.
registerCardEffect('GD05-124', {
  leagueMilitaireRestSubstitute: true,
});

// GD05-005 Strike Rouge (Ootori), GD05-013 Gun EZ: <Blocker> only, handled
// generically by the native-keyword mechanics — no registration needed.
// GD05-009 Gun Blaster, GD05-010 Kira's Strike Rouge, GD05-014 Javelin:
// vanilla Units with no effect text — no registration needed.

// ─── ST11-011 · Char Aznable — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// During Char's controller's own turn, all their friendly (Marine) Units
// get AP+1, for as long as this pilot remains paired/linked with any Unit —
// re-applies every one of the controller's own turns while Char stays
// paired (not just the turn he was paired), but NOT during the opponent's
// turn. Per user clarification (two rounds): first that it isn't scoped to
// only the pairing turn (an earlier charMarineBoostThisTurn flag, since
// removed, wrongly did that), then that it's additionally gated to the
// controller's own turn only — same ownerSide-relative isOwnerTurn check as
// ST07-007 Gundam Kyrios/GD06-085 Gym Ghingnham's "during your/opponent's
// turn" passives. globalPilotApBonus is only ever invoked for a sourceUnit
// that currently HAS this pilot paired (see getGlobalUnitApBonus in
// game.js), so no separate "is Char paired" check is needed here.
registerCardEffect('ST11-011', {
  globalPilotApBonus: (targetCard, pilot, sourceUnit, ownerSide) => {
    const isOwnerTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    if (!isOwnerTurn) return 0;
    return (targetCard.trait || '').includes('(Marine)') ? 1 : 0;
  },
});

// ─── ST11-001 · Char's Z'Gok ─────────────────────────────────────────────────
// [During Pair] While 2 or more OTHER friendly (Marine) Units are in play,
// enemy Units can't choose this Unit as their attack target. (Fires with any
// paired Pilot — no [During Link] gate, so link-req match isn't required.)
// [Deploy] If another friendly (Marine) Unit is already in play, choose 1
// enemy Unit that is Lv.2 or lower. Return it to the bottom of its owner's
// deck.
registerCardEffect('ST11-001', {
  passiveCantBeAttackTarget: (card) => {
    if (!card.pilot) return false;
    const ownerField = state.playerField.some(c => c && c.uid === card.uid) ? state.playerField : state.oppField;
    const otherMarineCount = ownerField.filter(c => c && c.uid !== card.uid && (c.trait || '').includes('(Marine)')).length;
    return otherMarineCount >= 2;
  },
  deploy: (card) => {
    const hasOtherMarine = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Marine)'));
    if (!hasOtherMarine) return;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit (Lv.2 or lower). Return it to the bottom of its owner's deck.", candidates, (target) => {
      if (target) {
        bounceEnemyUnitToDeckBottom(target);
        addLog(`${card.name} — returned ${target.name} to the bottom of its owner's deck.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── ST11-002 · Acguy ────────────────────────────────────────────────────────
// During your opponent's turn, while this Unit is rested, friendly (Marine)
// Units with 2 or less HP can't receive enemy effect damage. Checked live via
// passiveUnitGrantsEffectDamageImmunity (see dealDamageToUnit in game.js) —
// "your opponent's turn" is evaluated relative to THIS card's own owner
// (whichever field it's actually found on), not whichever client happens to
// be executing the hook, so it reads correctly from either side's mirror.
registerCardEffect('ST11-002', {
  passiveUnitGrantsEffectDamageImmunity: (card, target) => {
    const isMine = state.playerField.some(c => c && c.uid === card.uid);
    const isOwnersTurn = isMine ? state.myTurn : !state.myTurn;
    if (isOwnersTurn) return false; // must be during the OWNER's opponent's turn
    if (!card.rested) return false;
    if (!(target.trait || '').includes('(Marine)')) return false;
    return effectiveHp(target) <= 2;
  },
});

// ─── ST11-003 · Zock ─────────────────────────────────────────────────────────
// While another friendly (Marine) Unit is in play, this Unit gains <Blocker>.
// passiveKeywords re-evaluates live on every hasNativeKeyword check, same
// convention as GD02-076 Buster Gundam's AP-conditional Blocker. Uses
// cardOwnerSide (not always state.playerField) so this reads correctly
// whichever side's mirror it's checked from.
registerCardEffect('ST11-003', {
  passiveKeywords: (card) => {
    const side = cardOwnerSide(card);
    const friendlyField = side === 'opp' ? state.oppField : state.playerField;
    const hasAnotherMarine = friendlyField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Marine)'));
    return hasAnotherMarine ? ['Blocker'] : [];
  },
});

// ─── ST11-016 · Mad Angler ───────────────────────────────────────────────────
// [Burst] Deploy this card.
// [Deploy] Add 1 of your Shields to your hand — handled generically by
// applyBaseDeployEffect() (see ST06-015 Kaneban's identical comment). Then,
// if 4 or more enemy Units are in play, choose 1 rested enemy Unit. Deal 2
// damage to it.
registerCardEffect('ST11-016', {
  deploy: (card) => {
    if (state.oppField.filter(c => c).length < 4) return;
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — 4+ enemy Units in play, but none are rested. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name, 'Choose 1 rested enemy Unit. Deal 2 damage to it.', candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 2, { fromEnemy: true });
          render(); setupDropZones();
        }
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST11-014 · The Orca of Red Sea ─────────────────────────────────────────
// [Action] Choose 1 friendly (Marine) Unit. Enemy Units can't choose it as
// their attack target this turn.
// Uses the generic 'CantBeAttackTarget' tempKeyword — checked alongside the
// permanent passiveCantBeAttackTarget hook (ST11-001 Char's Z'Gok) in
// getValidAttackTargets(). Set on the target (my own card) and mirrored via
// TEMP_KEYWORD_ADD so the ATTACKING client (who builds its own attacker's
// target list against this card, mirrored on their oppField) excludes it too
// — same NET message TEMP_KEYWORD_ADD already used by ST06-013/ST03-014's
// battle-immunity tempKeywords.
registerCardEffect('ST11-014', {
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Marine)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Marine) Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      "Choose 1 friendly (Marine) Unit. Enemy Units can't choose it as their attack target this turn.",
      candidates,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('CantBeAttackTarget');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CantBeAttackTarget' });
        addLog(`${card.name} — enemy Units can't choose ${target.name} as their attack target this turn.`);
        render();
        setupDropZones();
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-002 · Infinite Justice Gundam ─────────────────────────────────────
// [Once per Turn] During your turn, when this Unit destroys an enemy card
// with battle damage, choose 1 of your (Orb) Units. It recovers 3 HP.
// [Deploy] You may choose 3 Unit cards from your trash. Exile them from the
// game. If you do, choose 1 active enemy Unit. Return it to its owner's
// hand.
// destroyerSide === 'player' guard: see ST12-003 (red.js) — onUnitDestroysEnemy
// fires unconditionally for whichever side's card scored the kill. The
// [Deploy] exile-3/bounce-1 flow gates on both preconditions (3+ Unit cards
// in trash, 1+ active enemy Unit) up front — matching resolveDevelopmentCost's
// (green.js) exact-N skippable multi-select + exileCardsFromTrash (purple.js)
// shape, then bounceEnemyUnit (white.js) for the follow-up.
registerCardEffect('GD06-002', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (card.passiveUsedThisTurn) return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Orb)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — [Once per Turn] no friendly (Orb) Units in play. No effect.`);
      return;
    }
    card.passiveUsedThisTurn = true;
    showCardPickerModal(card.name, 'Choose 1 of your (Orb) Units. It recovers 3 HP.', candidates, (target) => {
      if (target) {
        applyHpMod([target], 3);
        addLog(`${card.name} — [Once per Turn] ${target.name} recovers 3 HP.`);
        render();
      }
    }, card, { mandatory: true });
  },
  deploy: (card) => {
    const enemyActiveExists = state.oppField.some(c => c && !c.rested);
    const trashUnits = state.playerTrash.filter(c => c.type === 'unit');
    if (!enemyActiveExists || trashUnits.length < 3) return;
    showCardPickerModalMulti(
      card.name,
      'You may choose 3 Unit cards from your trash. Exile them from the game.',
      trashUnits,
      { min: 3, max: 3, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length < 3) return;
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — [Deploy] exiled ${chosen.map(c => c.name).join(', ')}.`);
        broadcastCounters();
        render();
        setupDropZones();
        const enemyCandidates = state.oppField.filter(c => c && !c.rested);
        if (enemyCandidates.length === 0) {
          addLog(`${card.name} — [Deploy] no active enemy Units. No effect.`);
          return;
        }
        showCardPickerModal(card.name, "Choose 1 active enemy Unit. Return it to its owner's hand.", enemyCandidates, (target) => {
          if (target) {
            bounceEnemyUnit(target);
            addLog(`${card.name} — [Deploy] returned ${target.name} to its owner's hand.`);
            render();
            setupDropZones();
          }
        }, card, { mandatory: true });
      },
      card
    );
  },
});

// ─── GD06-003 · GQuuuuuuX (Omega Psycommu Released) — Unit ──────────────────
// Increase this Unit's AP by an amount equal to the number of cards in your
// hand.
// [Attack] If another (Alphacide) Unit is in play and you have 5 or less
// cards in your hand, draw 1.
registerCardEffect('GD06-003', {
  passiveUnitApBonus: (card, ownerSide) =>
    ownerSide === 'player' ? state.hand.length : state.oppHandCount,
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const hasOtherAlphacide = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Alphacide)'));
    if (!hasOtherAlphacide || state.hand.length > 5) {
      addLog(`${card.name} — [Attack] condition not met. No effect.`);
      ctx.onDone?.(); return;
    }
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Attack] another (Alphacide) Unit in play, 5 or less cards in hand: drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Attack] deck is empty, no draw.`);
    }
    broadcastCounters(); render();
    ctx.onDone?.();
  },
});

// ─── GD06-004 · Gundam GP01 ──────────────────────────────────────────────────
// [During Link][Destroyed] If it is your turn, return this Unit's paired
// Pilot to its owner's hand. Then, discard 1.
// Same shape as GD01-005 Unicorn Gundam (Unicorn Mode)'s [During Link]
// [Destroyed] — isLinkUnit + destroyedOwnerSide gate, rescue the pilot back
// to hand, then a mandatory discard-1 picker — plus an extra "if it is your
// turn" gate state.myTurn is safe to read directly here since ownerSide ===
// 'player' already confirms this is running on the card owner's own client.
registerCardEffect('GD06-004', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    if (!isLinkUnit(card)) return;
    const ownerSide = destroyedOwnerSide(card, ctx);
    if (ownerSide !== 'player') return;
    if (!state.myTurn) return;
    if (!card.pilot) return;
    ctx.rescuePilot?.();
    const pilot = card.pilot;
    state.hand.push(pilot);
    updateTempState(pilot.cardNo, pilot.copyVer, 'hand');
    addLog(`${card.name} — ${pilot.name} returned to hand.`);
    broadcastCounters(); render();
    if (state.hand.length === 0) return;
    showCardPickerModal(
      card.name, 'Discard 1 card from your hand.',
      state.hand,
      (discarded) => {
        if (!discarded) { broadcastCounters(); render(); return; }
        state.hand = state.hand.filter(c => c.uid !== discarded.uid);
        updateTempState(discarded.cardNo, discarded.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(discarded));
        broadcastLog(`${card.name} — discarded ${discarded.name}.`);
        broadcastCardToast(`${discarded.name} was discarded due to ${card.name} effect.`, discarded.img);
        broadcastCounters(); render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD06-005 · Akatsuki (Shiranui) ──────────────────────────────────────────
// [Attack] You may choose 1 damaged enemy Unit that is Lv.5 or lower. Return
// it to its owner's hand.
registerCardEffect('GD06-005', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && c.damaged && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Attack] no damaged enemy Units Lv.5 or lower.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, "You may choose 1 damaged enemy Unit (Lv.5 or lower). Return it to its owner's hand.", candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — [Attack] returned ${target.name} to its owner's hand.`);
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD06-081 · Amate Yuzuriha (Machu) — Pilot ──────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit is (Alphacide), it gains <Repair 2>.
// CONFIRMED BUG: only pilotPassiveKeywords was registered, which drives the
// Repair icon/badge display (hasKwIcon()/hasNativeKeyword()) but NOT the
// actual end-of-turn heal — getRepairValue() (game.js) only reads
// pilotConditionalRepairValue for a pilot-granted Repair amount, so the
// linked Unit showed the Repair badge but never actually recovered any HP.
// Same two-hook pairing as GD01-087 Sayla Mass: pilotConditionalRepairValue
// drives the real heal, pilotPassiveKeywords drives the badge.
registerCardEffect('GD06-081', {
  pilotConditionalRepairValue: (pilot, unit) =>
    (unit.trait || '').includes('(Alphacide)') ? 2 : 0,
  pilotPassiveKeywords: (pilot, unit) =>
    (unit.trait || '').includes('(Alphacide)') ? ['Repair 2'] : [],
});

// ─── GD06-082 · Athrun Zala — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Choose 1 rested enemy Unit. Deal 1 damage to it.
// "When Paired" = any pairing (no isLinkUnit check) — same convention as
// ST01-010 Amuro Ray, just targeting rested enemy Units with damage instead
// of resting a low-HP one.
registerCardEffect('GD06-082', {
  pair: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Paired] no rested enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Paired] Choose 1 rested enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 batch — "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── ST10-011 · Kamille Bidan — Pilot ─────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] If 2 or more rested Units are in play, choose 1 enemy Unit
// whose Lv. is equal to or lower than this Unit. Rest it.
// "Units in play" counts both fields combined — same convention as EB01-002
// Hi-Nu Gundam (EX)'s "3 or more OTHER rested Units" check just above.
registerCardEffect('ST10-011', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    const restedCount = state.playerField.filter(c => c && c.rested).length
      + state.oppField.filter(c => c && c.rested).length;
    if (restedCount < 2) {
      addLog(`${card.name} — [When Linked] fewer than 2 rested Units in play. No effect.`);
      return;
    }
    const myLevel = parseInt(unit.level) || 0;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= myLevel);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Linked] no enemy Units at or below this Unit's Lv. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      "[When Linked] Choose 1 enemy Unit whose Lv. is equal to or lower than this Unit. Rest it.",
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── EB01-061 · Ellis Claude — Pilot ──────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] If a friendly (G Generation) Unit is in play, choose 1 enemy
// Unit that is Lv.3 or lower. Rest it.
// Plain [When Paired] (no Link qualifier) — fires on any pairing, Link or not.
registerCardEffect('EB01-061', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit) return;
    const hasGGen = state.playerField.some(c => c && (c.trait || '').includes('(G Generation)'));
    if (!hasGGen) {
      addLog(`${card.name} — [When Paired] no friendly (G Generation) Unit in play. No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Paired] no enemy Units Lv.3 or lower. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Paired] Choose 1 enemy Unit that is Lv.3 or lower. Rest it.',
      candidates,
      (target) => {
        if (!target) return;
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── EB01-062 · Jona Basta — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Attack][Once per Turn] Choose 1 enemy player. They may draw 1. If they
// draw with this effect, draw 1.
// Fires on both paired and linked attacks (no [During Link]/[During Pair]
// qualifier) — same as ST01-011 Suletta Mercury's [Attack][Once per Turn].
// New OFFER_DRAW/DRAW_RESPONSE round trip (offerDrawToOpponent, game.js) —
// only the enemy player has authoritative data for their own optional draw.
registerCardEffect('EB01-062', {
  attack: (card, ctx) => {
    if (card.passiveUsedThisTurn) { ctx.onDone?.(); return; }
    card.passiveUsedThisTurn = true;
    if (!state.networked) {
      addLog(`${card.name} — requires an opponent to choose; unavailable in solo practice.`);
      ctx.onDone?.();
      return;
    }
    offerDrawToOpponent((drew) => {
      if (drew) {
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length) {
          state.hand.push(drawn[0]);
          addLog(`${card.name} — opponent drew, so you also drew ${drawn[0].name}.`);
          broadcastCounters(); render();
        }
      } else {
        addLog(`${card.name} — opponent declined to draw. No effect.`);
      }
      ctx.onDone?.();
    });
  },
});

// ─── EB01-063 · Io Fleming — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// If there are 2 or more OTHER rested Units in play, this Unit gains <Repair 2>.
// pilotConditionalRepairValue drives end-of-turn Repair; pilotPassiveKeywords
// drives the Repair icon display via hasKwIcon() — same dual-hook convention
// as GD01-087 Sayla Mass above.
function eb01063RestedOtherCount(unit) {
  return state.playerField.filter(c => c && c.uid !== unit.uid && c.rested).length
    + state.oppField.filter(c => c && c.uid !== unit.uid && c.rested).length;
}
registerCardEffect('EB01-063', {
  pilotConditionalRepairValue: (pilot, unit) => eb01063RestedOtherCount(unit) >= 2 ? 2 : 0,
  pilotPassiveKeywords:        (pilot, unit) => eb01063RestedOtherCount(unit) >= 2 ? ['Repair 2'] : [],
});

// ─── EB01-064 · Rondo Gina Sahaku — Pilot ─────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit has Repair, it gains <Breach 1>.
// Reads the unit's CURRENT Repair value from any source (native keyword,
// another pilot's grant, EB01-063 above, etc.) via getRepairValue().
// CONFIRMED BUG (same class as GD06-081/GD01-092): only pilotPassiveKeywords
// was registered, which drives the Breach badge but NOT the actual combat
// bonus damage — getBreachValue() (game.js) only reads
// pilotConditionalBreachValue for a pilot-granted Breach amount.
registerCardEffect('EB01-064', {
  pilotConditionalBreachValue: (pilot, unit) => getRepairValue(unit) > 0 ? 1 : 0,
  pilotPassiveKeywords: (pilot, unit) => getRepairValue(unit) > 0 ? ['Breach 1'] : [],
});

// ─── ST11-013 · Poorly Planned Offensive — Command ────────────────────────
// [Burst] Choose 1 rested enemy Unit with 3 or less HP. Return it to its
// owner's hand.
// [Main]/[Action] Choose 1 rested enemy Unit with 3 or less HP. Return it to
// its owner's hand. If you do, draw 1.
function st11013Candidates() {
  return state.oppField.filter(c => c && c.rested && effectiveHp(c) <= 3);
}
function st11013MainAction(card, ctx) {
  const candidates = st11013Candidates();
  if (candidates.length === 0) {
    addLog(`${card.name} — no rested enemy Units with 3 or less HP. No effect.`);
    ctx.onDone();
    return;
  }
  showCardPickerModal(
    card.name,
    "Choose 1 rested enemy Unit with 3 or less HP. Return it to its owner's hand.",
    candidates,
    (target) => {
      if (target) {
        bounceEnemyUnit(target);
        const drawn = drawCardsFromTempDeck(1, true);
        drawn.forEach(c => state.hand.push(c));
        addLog(`${card.name} — returned ${target.name} to hand${drawn.length ? ` and drew ${drawn[0].name}` : ''}.`);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('ST11-013', {
  canActivate: () => st11013Candidates().length > 0,
  burst: (card, ctx) => {
    const candidates = st11013Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — [Burst] no valid target. No effect.`);
      ctx?.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      "[Burst] Choose 1 rested enemy Unit with 3 or less HP. Return it to its owner's hand.",
      candidates,
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(`${card.name} — [Burst] returned ${target.name} to hand.`);
          render(); setupDropZones();
        }
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
  main: st11013MainAction,
  action: st11013MainAction,
});

// ─── GD06-083 · Challia Bull (GQ) — Pilot ─────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] While this Unit has <Repair>, it gains <High-Maneuver>.
registerCardEffect('GD06-083', {
  pilotPassiveKeywords: (pilot, unit) => (isLinkUnit(unit) && getRepairValue(unit) > 0) ? ['High-Maneuver'] : [],
});

// ─── GD06-006 · GQuuuuuuX (Omega Psycommu) — Unit ─────────────────────────
// [Deploy] If another friendly (Alphacide) Unit is in play, you may choose 1
// enemy Unit with 3 or less HP. Return it to its owner's hand.
registerCardEffect('GD06-006', {
  deploy: (card) => {
    const hasOtherAlphacide = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Alphacide)'));
    if (!hasOtherAlphacide) return;
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less HP. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      "You may choose 1 enemy Unit with 3 or less HP. Return it to its owner's hand.",
      candidates,
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
          render();
          setupDropZones();
        }
      },
      card,
      { mandatory: false, skipLabel: 'Decline' }
    );
  },
});

// ─── GD06-102 · A Miracle — Command ───────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Action] If it is your opponent's turn and no enemy Units have attacked
// this turn, draw 2.
// state.attackOccurredThisTurn is set unconditionally inside
// registerAction('ATTACK', ...) in game.js and reset every turn transition
// (CLEAR_TURN_EFFECTS / UNREST_ALL) — see its declaration there.
registerCardEffect('GD06-102', {
  canActivate: () => !state.myTurn && !state.attackOccurredThisTurn,
  action: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(2, true);
    drawn.forEach(c => state.hand.push(c));
    addLog(`${card.name} — [Action] drew ${drawn.length} card(s).`);
    broadcastCounters(); render(); setupDropZones();
    ctx.onDone();
  },
});

// ════════════════════════════════════════════════════════════════════════════
// ST11-14 batch — newly added cards, "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── ST11-004 · ZnO — Unit ──────────────────────────────────────────────────
// [Deploy] Deploy 1 rested [GOOhN]((ZAFT)(Marine)･AP1･HP1) Unit token.
// T-028 is this exact token (AP1/HP1, (ZAFT)(Marine), source "Aquatic
// Assault [ST11]") — its `name` field was mistakenly "ZnO" (copy-paste from
// this very card) instead of "GOOhN"; fixed directly in the DB rather than
// adding a new duplicate token entry.
registerCardEffect('ST11-004', {
  deploy: (card) => {
    deployTokenToField('T-028', { rested: true });
    addLog(`${card.name} — [Deploy] deployed 1 rested [GOOhN] Unit token.`);
  },
});

// ─── GD06-008 · Powered GM — Unit ──────────────────────────────────────────
// [Attack] If a friendly (Earth Federation) Base is in play, this Unit gets
// AP+2 during this turn. Same self-buff-on-Attack shape as GD03-028 Auda's
// Maganac, just gated on a friendly Base trait instead of the attack target,
// and "this turn" (tempApMod) instead of "this battle" (battleApMod).
registerCardEffect('GD06-008', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    const hasEFBase = state.playerBase && (state.playerBase.trait || '').includes('(Earth Federation)');
    if (!hasEFBase) {
      addLog(`${card.name} — [Attack] no friendly (Earth Federation) Base in play. No effect.`);
      ctx?.onDone?.();
      return;
    }
    unit.tempApMod = (unit.tempApMod || 0) + 2;
    NET.send({ type: 'FIELD_AP_MOD', uids: [unit.uid], delta: 2, field: 'tempApMod' });
    addLog(`${card.name} — [Attack] friendly (Earth Federation) Base in play: AP+2 during this turn.`);
    render();
    ctx?.onDone?.();
  },
});

// ─── GD06-014 · GM Type C — Unit ────────────────────────────────────────────
// While 3 or more enemy Units are in play, this Unit gains <Blocker>.
// Same passive-Blocker shape as ST08-008 Gustav Karl Type-00.
registerCardEffect('GD06-014', {
  passiveKeywords: () =>
    state.oppField.filter(c => c).length >= 3 ? ['Blocker'] : [],
});

// ─── GD06-103 · Restore the Front Line — Command Pilot ──────────────────────
// [Main] Choose 1 rested enemy Unit. Deal 1 damage to it.
// [Pilot] [South Burning]
// Same shape as ST01-012 Thoroughly Damaged.
registerCardEffect('GD06-103', {
  canActivate: () => state.oppField.some(c => c && c.rested),
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 rested enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        dealDamageToUnit(target, 1, { fromEnemy: true });
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-122 · Albion — Base ──────────────────────────────────────────────
// [Burst] Deploy this card. (generic)
// [Deploy] Add 1 of your Shields to your hand. (generic) Then, choose 1
// enemy Unit that is Lv.2 or lower. Return it to its owner's hand.
registerCardEffect('GD06-122', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit that is Lv.2 or lower. Return it to its owner's hand.", candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-015 · Waldfeld's Gaia Gundam — Unit ───────────────────────────────
// [During Pair・(Orb) Pilot] This Unit gains <Blocker>.
// passiveKeywords — live, re-evaluated by hasNativeKeyword() every check, so
// the keyword appears/disappears automatically as the paired pilot changes,
// same convention as every other conditional-Blocker passive in this file.
registerCardEffect('GD06-015', {
  passiveKeywords: (card) => (card.pilot && (card.pilot.trait || '').includes('(Orb)')) ? ['Blocker'] : [],
});

// ─── GD06-104 · Steady Support — Command Pilot ──────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit with 3 or less HP. Return it to its
// owner's hand.
// [Pilot] [Chuck Keith]
function gd06104Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 3);
  pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 3 or less HP. Return it to its owner's hand.", onDone);
}
registerCardEffect('GD06-104', {
  canActivate: () => state.oppField.some(c => c && effectiveHp(c) <= 3),
  main: (card, ctx) => gd06104Effect(card, ctx.onDone),
  action: (card, ctx) => gd06104Effect(card, ctx.onDone),
});

// ─── GD06-001 · Gundam GP01Fb — Unit ────────────────────────────────────────
// [Once per Turn] When this Unit is attacked, draw 1.
// [During Pair] This Unit gains <High-Maneuver>.
// New 'defended' trigger (see registry.js and its firing site in
// registerAction('ATTACK') in game.js) — fires once per attack, only on the
// defending player's own client, only for a genuine Unit target. Reuses the
// generic passiveUsedThisTurn once-per-turn flag (cleared each turn by
// UNREST_ALL) since this card has no other ability competing for it.
registerCardEffect('GD06-001', {
  passiveKeywords: (card) => card.pilot ? ['High-Maneuver'] : [],
  defended: (card) => {
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — attacked: drew ${drawn[0].name}.`);
      broadcastCounters();
      render();
    } else {
      addLog(`${card.name} — attacked, but deck is empty. No draw.`);
    }
  },
});

// ─── GD06-080 · Kou Uraki — Pilot ───────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Activate･Action][Once per Turn] If you have drawn with an effect during
// this turn, choose 1 of your Units. It recovers 2 HP.
// Pilot [Activate･Action] convention (canActivate/activate + once-per-turn
// via card.passiveUsedThisTurn — see getActionEligiblePilots in game.js) —
// same shape as ST12-011 Milliadro Peacecraft, gated on
// state.effectDrawOccurredThisTurn instead of a battle-context condition.
registerCardEffect('GD06-080', {
  canActivate: () => state.effectDrawOccurredThisTurn && state.playerField.some(c => c),
  activate: (card, ctx) => {
    if (!state.effectDrawOccurredThisTurn) {
      addLog(`${card.name} — haven't drawn with an effect this turn. No effect.`);
      ctx.onDone?.(); return;
    }
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your Units. It recovers 2 HP.', candidates, (target) => {
      if (target) {
        applyHpMod([target], 2);
        addLog(`${card.name} — ${target.name} recovers 2 HP (now: ${effectiveHp(target)}).`);
        render(); setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── GD06-009 · GM Custom — Unit ─────────────────────────────────────────────
// [Deploy] You may choose 1 enemy Unit with 1 HP. Return it to its owner's
// hand. "1 HP" is an exact match, not a threshold — effectiveHp(c) === 1.
registerCardEffect('GD06-009', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) === 1);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with exactly 1 HP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit with 1 HP. Return it to its owner's hand.", candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD06-010 · GM Cannon II — Unit ──────────────────────────────────────────
// [When Linked] Draw 1. (As printed on the card art — nuCardDatabase.json's
// effectData currently reads "[During Link] Draw 1.", which would instead be
// the continuous-passive keyword, not a one-shot trigger; going by what's
// actually on the card here. Worth a look if that JSON field gets revisited.)
// Same pair + isLinkUnit-gate shape as GD05-004 Akatsuki (Oowashi)'s own
// self-linking trigger. Draw is kept local-only (addLog, not broadcastLog)
// since which specific card gets drawn is private — same convention as every
// other [Burst]/draw effect in this file.
registerCardEffect('GD06-010', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [When Linked] drew ${drawn[0].name}.`);
    }
    broadcastCounters(); render();
  },
});

// ─── GD06-017 · GQuuuuuuX — Unit ────────────────────────────────────────────
// While another friendly (Alphacide) Unit is in play, this Unit gains
// <Blocker>. Same passive-Blocker shape as ST11-003 Zock, re-evaluated live
// via passiveKeywords on every hasNativeKeyword check; cardOwnerSide (not
// always state.playerField) so it reads correctly from either side's mirror.
registerCardEffect('GD06-017', {
  passiveKeywords: (card) => {
    const side = cardOwnerSide(card);
    const friendlyField = side === 'opp' ? state.oppField : state.playerField;
    const hasOtherAlphacide = friendlyField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Alphacide)'));
    return hasOtherAlphacide ? ['Blocker'] : [];
  },
});

// ─── GD06-101 · The Gundam Says — Command ──────────────────────────────────
// [Burst] Choose 1 enemy Unit with 5 or less HP. Return it to its owner's
// hand.
// [Main]/[Action] Choose 1 enemy Unit with 5 or less HP. Return it to its
// owner's hand. Then if a friendly (Alphacide) Link Unit is in play, draw 1.
// Same shape as ST11-013 Poorly Planned Offensive, but the bonus draw is
// gated on board state (an (Alphacide) Link Unit in play) rather than on
// the bounce itself.
function gd06101Candidates() {
  return state.oppField.filter(c => c && effectiveHp(c) <= 5);
}
function gd06101MainAction(card, ctx) {
  const candidates = gd06101Candidates();
  if (candidates.length === 0) {
    addLog(`${card.name} — no enemy Units with 5 or less HP. No effect.`);
    ctx.onDone();
    return;
  }
  showCardPickerModal(
    card.name,
    "Choose 1 enemy Unit with 5 or less HP. Return it to its owner's hand.",
    candidates,
    (target) => {
      if (target) {
        bounceEnemyUnit(target);
        const hasAlphacideLink = state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(Alphacide)'));
        let drawn = [];
        if (hasAlphacideLink) {
          drawn = drawCardsFromTempDeck(1, true);
          drawn.forEach(c => state.hand.push(c));
        }
        addLog(`${card.name} — returned ${target.name} to its owner's hand${hasAlphacideLink ? (drawn.length ? ` and drew ${drawn[0].name}` : ' (friendly (Alphacide) Link Unit in play, but deck empty)') : ''}.`);
        if (drawn.length) broadcastCounters();
        render(); setupDropZones();
      }
      ctx.onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD06-101', {
  canActivate: () => gd06101Candidates().length > 0,
  burst: (card, ctx) => {
    const candidates = gd06101Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — [Burst] no enemy Units with 5 or less HP. No effect.`);
      ctx?.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      "[Burst] Choose 1 enemy Unit with 5 or less HP. Return it to its owner's hand.",
      candidates,
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(`${card.name} — [Burst] returned ${target.name} to its owner's hand.`);
          render(); setupDropZones();
        }
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
  main: (card, ctx) => gd06101MainAction(card, ctx),
  action: (card, ctx) => gd06101MainAction(card, ctx),
});

// ─── GD06-007 · Kikeroga (MA Mode) (GQ) — Unit ─────────────────────────────
// <Repair 2> (native, handled generically by getRepairValue/REPAIR_FIELD).
// [Deploy] Choose 1 rested enemy Unit. Deal 2 damage to it.
registerCardEffect('GD06-007', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested enemy Unit. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-011 · Red Gundam — Unit ──────────────────────────────────────────
// [When Linked] All your Units gain <Repair 2>.
// No "during this turn" qualifier and no one-time action verb (unlike the
// codebase's many "look at/choose/draw" [When Linked] triggers), so this
// reads as a continuous grant for as long as this Unit remains Linked — same
// live isLinkUnit() check used by every "[During Link]" passive (e.g.
// GD05-065 Landman Rodi), rather than a one-shot snapshot. passiveKeywords
// grants this Unit its own Repair 2; globalUnitKeyword propagates it to the
// rest of the field — same two-hook shape as GD01-001 Gundam.
registerCardEffect('GD06-011', {
  passiveKeywords: (card) => isLinkUnit(card) ? ['Repair 2'] : [],
  globalUnitKeyword: (targetCard, sourceCard, ownerSide) =>
    (ownerSide === 'player' && isLinkUnit(sourceCard)) ? ['Repair 2'] : [],
});
